import { z } from 'zod';

import { localeSchema } from './locales.js';
import { agentFlowRequestSchema } from './agent-flows.js';
import { WIZARD_MAX_CREATIVES } from './campaign-wizard.js';
import { CAPABILITY_MIN_ROLE, listQuerySchema, type Capability } from './common.js';
import { ROLE_RANK, type MessageRole, type WorkspaceRole } from './enums.js';
import type { AgentCard, DisconnectImpactView } from './agent-cards/index.js';

/**
 * AGENT — Claude tool-calling katmanı (§4.3).
 *
 * ---------------------------------------------------------------------------
 * Ekranın tasarım ekseni: ajan ASLA kendi başına para hareket ettirmez
 * ---------------------------------------------------------------------------
 * Yazma araçları HİÇBİR ŞEY UYGULAMAZ; bir ÖNERİ üretir ve sohbete onay kartı
 * olarak düşer. Onaylanınca aksiyon otomasyonla AYNI yoldan geçer — aynı
 * guardrail, aynı idempotency anahtarı, aynı execution log. Autopilot yetkisi
 * yalnızca Workflow modülünde.
 *
 * ---------------------------------------------------------------------------
 * Araçlar İNCE bir katman
 * ---------------------------------------------------------------------------
 * Her araç mevcut servis katmanını çağırır ve iş mantığını TEKRARLAMAZ.
 * `get_metrics` `AnalyticsQueryService`i çağırır; kendi SQL'ini yazan bir araç
 * bir gün ekrandan farklı sayı döndürür ve kullanıcı hangisine inanacağını
 * bilemez.
 *
 * ---------------------------------------------------------------------------
 * 🔴 LLM sağlayıcısı yapılandırılmamış
 * ---------------------------------------------------------------------------
 * `ANTHROPIC_API_KEY` .env'de YOK. `LLMProvider` soyutlaması, araç kayıt
 * defteri, konuşma kalıcılığı ve uçlar HAZIR olur; `NotConfiguredLLMProvider`
 * tipli `LLM_NOT_CONFIGURED` (HTTP 503) döner. Arayüz bunu görünce sohbet
 * kutusunu kilitler ve nedenini yazar.
 */

// ═══════════════════════════════════════════════════════════════════════════
// LLM sağlayıcı soyutlaması
// ═══════════════════════════════════════════════════════════════════════════

/**
 * `LLMProvider.stream(input): AsyncIterable<LLMStreamEvent>`
 *
 * Sağlayıcı bağımsız olay akışı. Anthropic'in kendi olay adlarını dışarı
 * sızdırmıyoruz ki sağlayıcı değişince arayüz değişmesin.
 */
export type LLMStreamEvent =
  | { kind: 'text_delta'; text: string }
  /**
   * ARAÇ ÇAĞRILARI ARASINDAKİ İLERLEME NOTU (29 Eyl 2026).
   *
   * Opus 5.5'te model araçlar arasındaki kısa notlarını ("kampanyaları okudum,
   * şimdi bütçeye bakıyorum") `text` değil `thinking` bloğu olarak döndürüyor
   * ve varsayılan görünümde metni BOŞ. `thinking.display: "updates"` ile o
   * notlar düz metin olarak geliyor; akıl yürütmenin kendisi gizli kalıyor.
   * Cevabın parçası DEĞİL — arayüz onu adımların yanında durum satırı olarak
   * basıyor. `block` aynı nota ait parçaları birleştirmek için.
   */
  | { kind: 'progress'; text: string; block: number }
  /**
   * ADSIZ CANLI DURUM (1 Eki 2026) — sağlayıcı bir sunucu aracı (web arama /
   * sayfa okuma) çalıştırmaya başladı. Araç adı TAŞIMAZ; ekran yalnız
   * "Araştırıyor" yazar (sohbette araç izi yok). Faz 9 X1: kod çalıştırma
   * (hesap, tablo, dosya üretimi) başladığında `computing` — "Hesaplıyor".
   */
  | { kind: 'activity'; activity: 'researching' | 'computing' | 'compacting' }
  /**
   * SUNUCU ARACI ADIMI (1 Eki 2026, canlı etkinlik paneli) — sağlayıcının
   * yürüttüğü web araması / sayfa okuma / kod çalıştırma / MCP çağrısı.
   * `start` girdi tamamlanınca (sorgu, adres, kod, sunucu adı), `result`
   * sonuç bloğu geldiğinde. HAM BLOK DEĞİL: yalnız panelin ihtiyacı, kısaltılmış.
   * Tur döngüsü bunu okunur bir adıma çeviriyor (`AgentActivityTracker`);
   * kod gövdesi ve çıktısı yalnız canlı akışta, kalıcı kayda girmiyor.
   * `id` sağlayıcının blok kimliği — eşleştirme için, ekrana gitmez.
   */
  | {
      kind: 'server_step';
      id: string;
      phase: 'start' | 'result';
      step: LLMServerStep;
      /** Yalnız `result`ta: sonuç hatasız mı. */
      ok?: boolean;
      /** Yalnız `result`ta: sağlayıcının hata kodu (`max_uses_exceeded`…) — kayıt için. */
      error?: string | null;
    }
  /**
   * WEB ARAÇLARI BU İSTEKTE REDDEDİLDİ (1 Eki 2026) — kuruluşta web araması
   * Console'dan kapalı (400 `invalid_request_error`, "web search is not
   * enabled"). Akış hiç başlamadı; sağlayıcı isteği web araçsız TEKRARLIYOR
   * ve tur döngüsü bir saatlik "kapalı" işaretini koyuyor. Kullanıcıya hata
   * gitmiyor. `notice` — sağlayıcının tekrarlanan isteğin son kullanıcı
   * mesajına eklediği not (modele "web şu an kapalı" diyor); tur döngüsü
   * AYNI metni kalıcı geçmişe yazıyor ki sonraki istekler aynı baytı görsün.
   * Eklenemediyse (son mesaj araç sonucuydu) `null`.
   *
   * `reason`: `disabled` = kuruluşta kapalı (bir saatlik GENEL işaret);
   * `rejected` = web araçlarını anan başka bir 400 (ZDR, sözleşme değişikliği)
   * — yalnız bu tur web'siz, genel işaret YOK (tek sohbetin sorunu herkesin
   * web'ini kapatmasın).
   */
  | { kind: 'web_unavailable'; reason: 'disabled' | 'rejected'; notice: string | null }
  /**
   * MÜŞTERİNİN MCP SUNUCUSU BU İSTEKTE DÜŞTÜ (1 Eki 2026) — sağlayıcı
   * bağlanamadı ya da yetki hatası aldı; istek o sunucu(lar) olmadan
   * TEKRARLANIYOR. `servers` = `mcp_servers[].name` (bağlantının `slug`ı).
   * Tur döngüsü bağlantı satırına hata + kısa duraklatma yazıyor ve `notice`
   * (modele giden not) kalıcı geçmişe ekleniyor — web notuyla aynı yol.
   */
  | {
      kind: 'mcp_unavailable';
      servers: string[];
      reason: 'unreachable' | 'unauthorized';
      notice: string | null;
    }
  /**
   * KOD ÇALIŞTIRMA BU İSTEKTE KULLANILAMADI (Faz 9 X1, 1 Eki 2026) — sağlayıcı
   * isteği kod araçları (kod çalıştırma, beceriler, kod içi araç çağırma,
   * konteyner yüklemeleri) OLMADAN tekrarlıyor; tur çökmüyor.
   *
   *   · `disabled` — kuruluşta kapalı / reddedildi (bir saatlik GENEL işaret)
   *   · `rejected` — bu isteğin kod parçası reddedildi (yalnız bu tur)
   *   · `container_expired` — sohbetin konteynerinin süresi doldu; istek YENİ
   *     konteynerle tekrarlandı (kod yine açık), eski kimlik unutulsun
   *
   * `notice` — modele giden not (son kullanıcı mesajına eklendiyse); web
   * notuyla aynı yol: tur döngüsü kalıcı geçmişe yazıyor.
   */
  | { kind: 'code_unavailable'; reason: 'disabled' | 'rejected' | 'container_expired'; notice: string | null }
  /**
   * KONTEYNER (Faz 9 X1) — yanıtın `container` alanı: kod çalıştırmanın
   * kullandığı sandbox. Tur döngüsü kimliği saklıyor; sohbetin sonraki
   * istekleri AYNI konteyneri kullanıyor (dosyalar, Python durumu).
   */
  | { kind: 'container'; id: string; expiresAt: string | null }
  /**
   * SAĞLAYICIDA BULUNAMAYAN DOSYA (Faz 9 X1) — geçmişteki `file_id`
   * (Files API) silinmiş/süresi dolmuş; sağlayıcı isteği o dosyalar yerine
   * tek satırlık notla tekrarladı. Tur döngüsü dosyaları "gitti" işaretliyor
   * ki sonraki istekler eski yola (base64 / satır) düşsün.
   */
  | { kind: 'files_missing'; fileIds: string[] }
  /**
   * ARAÇ ÇAĞRISI. `caller` (Faz 9 X1): `code` = model aracı KOD ÇALIŞTIRMANIN
   * İÇİNDEN çağırdı (programatik araç çağırma); yoksa doğrudan. Kod içi
   * çağrı yalnız okuma araçlarında yürütülür, kart çizmez.
   */
  | { kind: 'tool_use'; toolCallId: string; name: string; input: unknown; caller?: 'direct' | 'code' }
  | { kind: 'tool_result'; toolCallId: string; ok: boolean; result: unknown; error?: string }
  | {
      kind: 'usage';
      /// İsteğin TOPLAM girdisi: önbellekten okunan + yazılan + önbelleksiz.
      inputTokens: number;
      outputTokens: number;
      cacheReadTokens?: number;
      cacheCreationTokens?: number;
      /// Bu istekte sağlayıcının yürüttüğü web araması / sayfa okuma sayısı
      /// (`usage.server_tool_use`) — günlük tavan sayacı bunu okuyor.
      webSearchRequests?: number;
      webFetchRequests?: number;
      /// Bu istekte sağlayıcının yürüttüğü kod çalıştırma sayısı (Faz 9 X1) —
      /// günlük kod tavanı sayacı bunu okuyor.
      codeExecutionRequests?: number;
      /**
       * SUNUCU TARAFI SIKIŞTIRMA (Faz 9 X2) — bu istekte özet üretildiyse o
       * yinelemenin jetonları. Sağlayıcının üst düzey `usage`ı onları İÇERMİYOR
       * (`usage.iterations`); yukarıdaki toplamlara EKLENDİ, burada ayrıca.
       */
      compaction?: { inputTokens: number; outputTokens: number };
      /**
       * DANIŞMAN MODEL (Faz 9 X2) — danışmanın kendi fiyatıyla faturalanan
       * jetonları (`usage.iterations[type=advisor_message]`). Yürütücü
       * toplamlarına KATILMIYOR (farklı model, farklı fiyat).
       */
      advisor?: { model: string; inputTokens: number; outputTokens: number; calls: number };
      /// Message Batches ile koştu (%50 fiyat) — maliyet kaydı için.
      batch?: boolean;
      /// Hızlı kip (`speed: "fast"`) gerçekten uygulandı mı (`usage.speed`).
      fast?: boolean;
    }
  /**
   * ASİSTAN TURUNUN KENDİSİ — sağlayıcının döndürdüğü içerik blokları, AYNEN.
   *
   * 🔴 KORUNAN DÜŞÜNME (preserved thinking): Opus 5.5 düşünme bloklarını
   * imzalı döndürüyor ve bir sonraki istekte AYNEN geri istiyor; imza modeli
   * ve konuşmanın o ana kadarki önekini bağlıyor. Blokları metin + araç
   * olaylarından YENİDEN KURMAK onları kaybetmek demek. Bu olay turun sonunda
   * bir kez gelir ve saklanacak/gönderilecek içerik budur.
   *
   * Olmayabilir (test ikizleri, eski sağlayıcılar): o zaman tüketici blokları
   * `text_delta` + `tool_use` olaylarından kuruyor — eski davranış.
   */
  | {
      kind: 'message';
      content: unknown[];
      model: string;
      /// Sağlayıcının mesaj kimliği (`msg_…`) — önbellek teşhisi bir sonraki
      /// istekte bunu `diagnostics.previous_message_id` olarak gönderiyor.
      id?: string;
    }
  | {
      kind: 'stop';
      reason:
        | 'end_turn'
        | 'max_tokens'
        | 'tool_use'
        | 'canceled'
        | 'error'
        /// Güvenlik sınıflandırıcısı reddetti (yedek model de reddettiyse). Yarım içerik ATILIR.
        | 'refusal'
        /// Sunucu tarafı araç döngüsü (araç arama) duraksadı — içerik geri gönderilip sürdürülür.
        | 'pause_turn';
    }
  | { kind: 'error'; code: string; message: string };

/**
 * Sunucu aracı adımının panel için ayıklanmış hâli (`server_step` olayı).
 * Metinler sağlayıcıda kısaltılıyor; kişisel veri kemeri tur döngüsünde.
 */
export type LLMServerStep =
  | { tool: 'web_search'; query: string | null; results?: { url: string; title: string | null }[] }
  | { tool: 'web_fetch'; url: string | null; title?: string | null }
  | {
      tool: 'code';
      /** `file` = metin düzenleyicisiyle dosya oluşturma/düzenleme. */
      language: 'python' | 'bash' | 'file';
      code: string | null;
      /** `file` dilinde dosyanın yolu. */
      path?: string | null;
      output?: string | null;
      /** Sonuçta üretilen dosya sayısı. */
      files?: number;
    }
  | { tool: 'mcp'; server: string | null };

/**
 * Varsayılan model. `AGENT_MODEL` env anahtarı ile ezilebilir.
 *
 * 29 Eyl 2026: Opus 5.5 — düşünme kapatılamıyor (efor tek kontrol), zorunlu
 * `tool_choice` 400, düşünme blokları konuşmaya bağlı. Sağlayıcı bu üçünü
 * `anthropic.llm.provider.ts` içinde karşılıyor.
 */
export const DEFAULT_AGENT_MODEL = 'claude-opus-5-5';

// ═══════════════════════════════════════════════════════════════════════════
// Araç kayıt defteri
// ═══════════════════════════════════════════════════════════════════════════

export const AGENT_TOOLS = [
  'get_metrics',
  'get_campaigns',
  'analyze_campaigns',
  'get_connections',
  'get_credit_balance',
  'list_workflows',
  'adjust_budget',
  'pause_campaign',
  'generate_creative',
  // ── Genişleme Adım 3 ────────────────────────────────────────────────────
  // Katalog sağlığı okuma + metin düzeltme önerisi.
  //
  // İkisi AYRI araç ve bu zorunlu: düzeltme aracı ürünün MEVCUT sorunlarını
  // bilmeden çağrılırsa model neyi neden düzelttiğini söyleyemez ve
  // kullanıcı onay kutusunda gerekçesiz bir metin görür.
  'get_catalog_health',
  // `write` — HİÇBİR ŞEY UYGULAMIYOR, öneri üretiyor. Mağazaya yazma yalnız
  // kullanıcı onayından sonra.
  'fix_product_content',
  // ── Tek AI yüzeyi, Faz 1: katalog gözü ──────────────────────────────────
  //
  // Üçü de OKUMA. Amaç, ajanın "hangi ürün", "hangi şablon", "hangi manken"
  // sorularını KENDİ cevaplayabilmesi: bu bilgiler bugüne kadar yalnız kreatif
  // tezgâhının ve kampanya direktörünün sistem prompt'una gömülüydü, yani ajan
  // sohbetinde erişilemiyordu.
  //
  // 🔴 Sıra SONA ekleniyor ve öyle kalmalı. Araç tanımları prompt önekinin
  // başında render ediliyor ve o önek artık ÖNBELLEKLENİYOR
  // (`anthropic.llm.provider.ts` → `cache_control`); araya bir araç sokmak
  // önbelleği her istekte boşa çıkarır.
  'list_products',
  'get_product',
  'list_creative_catalog',
  // ── Faz 2: sıra sıra ilerleme ───────────────────────────────────────────
  //
  // Kullanıcının isteği: *"takıldığı her yerde soru sorup ilerletebilecek."*
  // Bu araç o cümlenin karşılığı ve kampanya kipine değil BÜTÜN SOHBETE ait.
  'ask_user',
  /**
   * Kütüphane + SÜREN İŞLER, tek araçta.
   *
   * Kullanıcı "görsellerim" derken hazır olanı da üretilmekte olanı da
   * kastediyor; ikisini iki araca bölmek her "hazır mı?" sorusunda modele
   * yanlış aracı seçme şansı verirdi.
   */
  'list_creatives',
  // ── Faz 4: kampanya ─────────────────────────────────────────────────────
  //
  // TEK araç, TEK onay kartı, ÜÇ platform çağrısı. Üç ayrı yazma aracı
  // yazılmadı: kullanıcı ikisini onaylayıp üçüncüyü reddettiğinde platformda
  // reklamsız bir ad set kalırdı ve o yarım nesnenin neden orada olduğu
  // hiçbir ekranda yazmazdı.
  'create_campaign',
  // ── Tek AI yüzeyi, Faz 5: "sitede ne yapılıyorsa sohbette de" ─────────
  //
  // 19 Eyl 2026, kullanıcı isteği: *"ai her şeyi yapabilmeli, sitede ne
  // yapabiliyorsa burda da yapabilmeli"*. Dördü de panelde ZATEN var olan bir
  // işin sohbetteki karşılığı; hiçbiri yeni bir yetenek icat etmiyor.
  //
  // 🔴 SONA eklendi, araya değil — önbellek gerekçesi yukarıda.
  /**
   * KAMPANYA — `/campaigns/new` brief'inin kendisi (`campaignBriefSchema`).
   *
   * `create_campaign`in yerini alıyor: o araç yalnız Meta'yı biliyordu ve
   * panel 17 Eyl 2026'da tek brief → çok platform akışına geçmişti. Ajan
   * "Twitter için kampanya kuramam" diyordu, oysa sayfa kurabiliyordu.
   * Aynı şema, aynı derleyici (`planBrief`), aynı yayınlayıcı
   * (`CampaignBriefService.launch`) — ikinci bir kampanya kurma yolu YOK.
   */
  'plan_campaign',
  /**
   * YAYINA AL — duraklatmanın tersi. Ajan "benim yayına alma yetkim yok"
   * diyordu (25 Ağu 2026, kayıtta); panelde kampanya satırının anahtarı bunu
   * tek dokunuşla yapıyor. Otomasyonun `resume` uygulayıcısından geçiyor.
   */
  'resume_campaign',
  /// Mağaza siparişleri — panelin Siparişler ekranı.
  'list_orders',
  /// Google anahtar kelimeleri — panelin Anahtar kelimeler ekranı.
  'list_keywords',
  // ── Faz 8 dalga 2 · B kampanya kartları (30 Eyl 2026) ────────────────────
  // İkisi de OKUMA ve bir KART çiziyor; düğmesi insanın kararı (kart eylemi).
  // 🔴 SONA eklendi (önbellek öneki). Gerekçe `apps/api/src/agent/cards/campaign/tools.ts`.
  /// Google anahtar kelime fikirleri — hedef reklam grubuyla, "seçilenleri ekle" kartta.
  'keyword_ideas',
  /// Gün-saat takvimi — mevcut takvim + (Google) saat × gün ısısı, kartta düzenle/uygula.
  'ad_schedule',
] as const;
export type AgentToolName = (typeof AGENT_TOOLS)[number];

export interface AgentToolDefinition {
  name: AgentToolName;
  /**
   * `read` araçlar doğrudan çalışır ve sonucu döner.
   * `write` araçlar HİÇBİR ŞEY UYGULAMAZ — `AgentProposal` üretir.
   * `ask` araçlar TURU DURDURUR: kullanıcıya bir soru bırakıp beklerler.
   *
   * Üçüncü tür ayrı bir kavram çünkü döngüdeki davranışı ötekilerin ikisine de
   * benzemiyor: sonuç dönüyor ama model onunla devam ETMİYOR — cevap bir
   * sonraki turda, kullanıcıdan geliyor.
   */
  kind: 'read' | 'write' | 'ask';
  /// Aracı çağırabilmek için gereken en düşük workspace rolü.
  minRole: 'VIEWER' | 'MEMBER' | 'ADMIN' | 'OWNER';
  /// Hangi servisi çağırıyor — denetlenebilirlik için sözleşmede yazılı.
  backedBy:
    | 'analytics'
    | 'integrations'
    | 'automation'
    | 'creative'
    | 'billing'
    | 'commerce'
    | 'agent'
    | 'rmf';
  descriptionKey: string;
  /// Arayüz kapatabilsin diye; bugün hepsi açık (kalıcı bayrak deposu yok).
  enabled: boolean;
}

export const AGENT_TOOL_REGISTRY: Record<AgentToolName, AgentToolDefinition> = {
  get_metrics: {
    name: 'get_metrics',
    kind: 'read',
    minRole: 'VIEWER',
    backedBy: 'analytics',
    descriptionKey: 'agent.tools.get_metrics',
    enabled: true,
  },
  get_campaigns: {
    name: 'get_campaigns',
    kind: 'read',
    minRole: 'VIEWER',
    backedBy: 'analytics',
    descriptionKey: 'agent.tools.get_campaigns',
    enabled: true,
  },
  analyze_campaigns: {
    name: 'analyze_campaigns',
    kind: 'read',
    minRole: 'VIEWER',
    backedBy: 'analytics',
    descriptionKey: 'agent.tools.analyze_campaigns',
    enabled: true,
  },
  get_connections: {
    name: 'get_connections',
    kind: 'read',
    minRole: 'VIEWER',
    backedBy: 'integrations',
    descriptionKey: 'agent.tools.get_connections',
    enabled: true,
  },
  get_credit_balance: {
    name: 'get_credit_balance',
    kind: 'read',
    minRole: 'VIEWER',
    backedBy: 'billing',
    descriptionKey: 'agent.tools.get_credit_balance',
    enabled: true,
  },
  list_workflows: {
    name: 'list_workflows',
    kind: 'read',
    minRole: 'VIEWER',
    backedBy: 'automation',
    descriptionKey: 'agent.tools.list_workflows',
    enabled: true,
  },
  adjust_budget: {
    name: 'adjust_budget',
    kind: 'write',
    minRole: 'MEMBER',
    backedBy: 'automation',
    descriptionKey: 'agent.tools.adjust_budget',
    enabled: true,
  },
  pause_campaign: {
    name: 'pause_campaign',
    kind: 'write',
    minRole: 'MEMBER',
    backedBy: 'automation',
    descriptionKey: 'agent.tools.pause_campaign',
    enabled: true,
  },
  generate_creative: {
    name: 'generate_creative',
    kind: 'write',
    minRole: 'MEMBER',
    backedBy: 'creative',
    descriptionKey: 'agent.tools.generate_creative',
    enabled: true,
  },
  get_catalog_health: {
    name: 'get_catalog_health',
    kind: 'read',
    minRole: 'VIEWER',
    backedBy: 'commerce',
    descriptionKey: 'agent.tools.get_catalog_health',
    enabled: true,
  },
  fix_product_content: {
    name: 'fix_product_content',
    kind: 'write',
    minRole: 'MEMBER',
    backedBy: 'commerce',
    descriptionKey: 'agent.tools.fix_product_content',
    enabled: true,
  },

  /**
   * 🔴 `minRole` ELLE SEÇİLMİYOR — yeteneğin eşiğiyle aynı olmak ZORUNDA.
   *
   * `list_products` ve `get_product` `report.read` (VIEWER),
   * `list_creative_catalog` `creative.read` (VIEWER) altında yaşıyor
   * (`CAPABILITY_MIN_ROLE`). Buraya daha yüksek bir rol yazmak, ekrandan
   * okuyabildiği bir şeyi ajana soramayan bir kullanıcı üretirdi — ve o farkı
   * hiçbir ekran açıklayamazdı.
   */
  list_products: {
    name: 'list_products',
    kind: 'read',
    minRole: 'VIEWER',
    backedBy: 'commerce',
    descriptionKey: 'agent.tools.list_products',
    enabled: true,
  },
  get_product: {
    name: 'get_product',
    kind: 'read',
    minRole: 'VIEWER',
    backedBy: 'commerce',
    descriptionKey: 'agent.tools.get_product',
    enabled: true,
  },
  list_creative_catalog: {
    name: 'list_creative_catalog',
    kind: 'read',
    minRole: 'VIEWER',
    backedBy: 'creative',
    descriptionKey: 'agent.tools.list_creative_catalog',
    enabled: true,
  },
  /**
   * SORU SORMA — hiçbir servisi çağırmıyor, `backedBy: 'agent'`.
   *
   * `minRole: 'VIEWER'`: soru sormak bir yetki gerektirmez. Yazma yetkisi
   * olmayan bir izleyicinin de ajana danışabilmesi gerekiyor.
   */
  list_creatives: {
    name: 'list_creatives',
    kind: 'read',
    minRole: 'VIEWER',
    backedBy: 'creative',
    descriptionKey: 'agent.tools.list_creatives',
    enabled: true,
  },
  /**
   * `backedBy: 'rmf'` — kampanya/ad set/reklam oluşturan tek yol.
   *
   * `minRole: 'MEMBER'`, çünkü `campaign.write` yeteneğinin eşiği o
   * (`CAPABILITY_MIN_ROLE`). Daha yükseğe çekmek, ekrandaki sihirbazdan
   * kampanya açabilen ama ajandan açamayan bir rol üretirdi.
   */
  /**
   * 🔴 KAPALI (19 Eyl 2026) — yerini `plan_campaign` aldı.
   *
   * Tanım SİLİNMEDİ: eski sohbetlerde bu araçla üretilmiş kartlar duruyor ve
   * `AgentProposalService.approve` onları hâlâ tanıyor (`applyCampaign`).
   * Kapalı araç modele HİÇ tanıtılmıyor (`toolsForRole`), yani yeni kart
   * doğmuyor; var olanlar okunur ve onaylanabilir kalıyor.
   */
  create_campaign: {
    name: 'create_campaign',
    kind: 'write',
    minRole: 'MEMBER',
    backedBy: 'rmf',
    descriptionKey: 'agent.tools.create_campaign',
    enabled: false,
  },
  ask_user: {
    name: 'ask_user',
    kind: 'ask',
    minRole: 'VIEWER',
    backedBy: 'agent',
    descriptionKey: 'agent.tools.ask_user',
    enabled: true,
  },
  /**
   * `minRole` `create_campaign` ile aynı (`campaign.write` → MEMBER).
   * `backedBy: 'rmf'`: yayınlayıcı `CampaignBriefService`, o da `RmfService`in
   * üstünde duruyor.
   */
  plan_campaign: {
    name: 'plan_campaign',
    kind: 'write',
    minRole: 'MEMBER',
    backedBy: 'rmf',
    descriptionKey: 'agent.tools.plan_campaign',
    enabled: true,
  },
  /// Duraklatmayla aynı eşik ve aynı yol — biri yapabiliyorsa öteki de.
  resume_campaign: {
    name: 'resume_campaign',
    kind: 'write',
    minRole: 'MEMBER',
    backedBy: 'automation',
    descriptionKey: 'agent.tools.resume_campaign',
    enabled: true,
  },
  list_orders: {
    name: 'list_orders',
    kind: 'read',
    minRole: 'VIEWER',
    backedBy: 'commerce',
    descriptionKey: 'agent.tools.list_orders',
    enabled: true,
  },
  list_keywords: {
    name: 'list_keywords',
    kind: 'read',
    minRole: 'VIEWER',
    backedBy: 'analytics',
    descriptionKey: 'agent.tools.list_keywords',
    enabled: true,
  },
  /* Katalog OKUMA eşiği (`campaign.read`, VIEWER); kartın ekle düğmesi yazma
     rolünü kendisi istiyor (kart eylemi politikası). */
  keyword_ideas: {
    name: 'keyword_ideas',
    kind: 'read',
    minRole: 'VIEWER',
    backedBy: 'integrations',
    descriptionKey: 'agent.tools.keyword_ideas',
    enabled: true,
  },
  ad_schedule: {
    name: 'ad_schedule',
    kind: 'read',
    minRole: 'VIEWER',
    backedBy: 'integrations',
    descriptionKey: 'agent.tools.ad_schedule',
    enabled: true,
  },
};

/**
 * 🔴 Araç girdisindeki `workspaceId` İSTEMCİDEN DEĞİL OTURUMDAN gelir.
 *
 * Modelin ürettiği araç girdisi kullanıcı metninden etkilenebilir. Kapsam
 * parametresini modele bırakmak, "başka bir workspace'in verisini getir"
 * yazan bir kullanıcının bunu başarması demektir. Araç çağrıları workspace
 * guard'ından geçer ve kapsam SUNUCUDA enjekte edilir.
 */
export const TOOL_SCOPE_COMES_FROM_SESSION = true;

// ═══════════════════════════════════════════════════════════════════════════
// Operatör araçları — sohbette VE arka plan operatöründe (29 Eyl 2026)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * AI'nın kalıcı hafızası ve kendi zamanlayıcısı (`shared/operator.ts`).
 *
 * ═══════════════════════════════════════════════════════════════════════
 * Neden `AGENT_TOOLS`a GİRMİYOR
 * ═══════════════════════════════════════════════════════════════════════
 * `AGENT_TOOLS` arayüzde `Record<AgentToolName, …>` ile TAM tiplenen bir
 * liste (ikon sözlüğü, çeviri anahtarları, `agent-i18n.spec.ts`). Operatör
 * araçları ayrı bir sözlük: arayüz onları Faz 3'te ekliyor; o zamana kadar
 * adım şeridi ham adı basıyor (yedek yol zaten var).
 *
 * `notify_team` yalnız başsız koşuda (operatör) modele tanıtılıyor: sohbette
 * kullanıcı zaten karşısında.
 */
export const AGENT_OPERATOR_TOOLS = [
  'remember',
  'forget',
  'list_notes',
  'schedule_task',
  'cancel_task',
  'list_tasks',
  'notify_team',
] as const;
export type AgentOperatorToolName = (typeof AGENT_OPERATOR_TOOLS)[number];

export const AGENT_OPERATOR_TOOL_REGISTRY: Record<
  AgentOperatorToolName,
  { kind: 'read' | 'write'; minRole: 'VIEWER' | 'MEMBER' | 'ADMIN'; headlessOnly: boolean }
> = {
  /* 🔴 Faz 4 (29 Eyl 2026) — yazan araçlar ADMIN: REST karşılıkları
     (`POST/DELETE /operator/notes|tasks`) `automation.write` (ADMIN) istiyor.
     MEMBER iken sohbet üzerinden not yazıp operatörün kararlarını ("günlük
     2000 TL'yi geçme") yönlendirmek ya da bir yöneticinin görevini iptal
     etmek, REST'in kapattığı kapıyı modelden açmaktı. Liste araçları REST'le
     aynı eşikte (`agent.read` = VIEWER) — yeni bir okuma yüzeyi açmıyorlar. */
  /* Faz 8 E (30 Eyl 2026): `remember` MEMBER'a GÖRÜNÜR ama yazmaz —
     yürütücü (`AgentOperatorToolService`) yönetici olmayanı DÜRÜSTÇE
     reddediyor (`MEMORY_ADMIN_ONLY`): model "hatırlayacağım" deyip
     kaydetmemek yerine sebebi söylesin. Ortak hafıza operatörün (ADMIN)
     kararlarını yönlendiriyor — Faz 4 kapısı AÇILMADI. */
  remember: { kind: 'write', minRole: 'MEMBER', headlessOnly: false },
  forget: { kind: 'write', minRole: 'ADMIN', headlessOnly: false },
  list_notes: { kind: 'read', minRole: 'VIEWER', headlessOnly: false },
  /* Görev bir operatör koşusu doğuruyor. Faz 8 E (denetim #39): MEMBER da
     kurabilir — ama yönetici olmayanın görevi SALT OKUMA/ÖNERİ koşusu
     (`OperatorRunnerService`: yazma aracı yok, sonuç kaynak sohbete) ve
     MEMBER yalnız KENDİ görevini iptal eder (`OperatorTasksService.cancel`). */
  schedule_task: { kind: 'write', minRole: 'MEMBER', headlessOnly: false },
  cancel_task: { kind: 'write', minRole: 'MEMBER', headlessOnly: false },
  list_tasks: { kind: 'read', minRole: 'VIEWER', headlessOnly: false },
  notify_team: { kind: 'write', minRole: 'ADMIN', headlessOnly: true },
};

/**
 * Platform Operasyon Kataloğundan (POK) gelen bir yazma operasyonunun onay
 * kartı — `AgentProposal.tool` değeri ve `WorkflowActionLog.actionType`.
 *
 * Tek bir genel kart: katalog yüzlerce operasyon taşıyor ve her biri için
 * ayrı bir kart türü yazmak imkânsız. Kartın başlığı ve alanları operasyonun
 * kendisinden (`AgentPlatformOpDraft`, `agent-draft.ts`).
 */
export const PLATFORM_OP_PROPOSAL_TOOL = 'platform_op' as const;

/**
 * İŞLEM PLANI kartı (Faz 5, 29 Eyl 2026) — sıralı en fazla 25 katalog
 * operasyonu TEK onayla. Sonraki adımın parametresi önceki adımın sonucuna
 * başvurabiliyor (`$1.created[0].platformRef`) — model kimlik beklemeden
 * kampanya → reklam seti → reklam zincirini tek kartta kuruyor. Kartın
 * içeriği `after.draft` (`AgentPlatformOpPlanDraft`, `agent-draft.ts`).
 */
export const PLATFORM_OP_PLAN_PROPOSAL_TOOL = 'platform_op_plan' as const;

/**
 * MENTORIVO EKRAN İŞLEMİ kartı (Faz 7, 30 Eyl 2026) — otomasyon, ekip,
 * bağlantı, kreatif stüdyo… gibi panelin KENDİ ekranlarındaki yazmalar.
 * İçerik `after.draft` (`AgentAppOpDraft`); hangi ekran işlemi olduğu
 * `draft.tool` (`AGENT_APP_TOOLS`).
 */
export const APP_OP_PROPOSAL_TOOL = 'app_op' as const;

export type AgentProposalTool =
  | AgentToolName
  | typeof PLATFORM_OP_PROPOSAL_TOOL
  | typeof PLATFORM_OP_PLAN_PROPOSAL_TOOL
  | typeof APP_OP_PROPOSAL_TOOL;

/**
 * PLAN ARAÇLARI — modele tanıtılan, `AGENT_TOOLS`TA OLMAYAN iki yazma aracı
 * (Faz 5). Neden ayrı sözlük: `AGENT_TOOLS` arayüzde tam tiplenen bir liste
 * (ikon, çeviri, `agent-i18n.spec.ts`); operatör araçları gibi bunlar da
 * arayüze kendi hızında giriyor (adım şeridi ham adı basıyor).
 *
 *   · `platform_op_plan`        — sıralı katalog operasyonları, TEK kart.
 *   · `build_customer_audience` — bağlı mağazanın müşterilerinden (ya da
 *     platform lead'lerinden) platform kurallarına göre hash'lenmiş müşteri
 *     listesi kitlesi / çevrimdışı dönüşüm planı. `DATA_SHARING`: HER ZAMAN
 *     insan onayı.
 *
 * İkisi de ÖNERİ üretir; hiçbir şey uygulamaz (`agent-tool.no-execute.spec.ts`).
 */
export const AGENT_PLAN_TOOLS = ['platform_op_plan', 'build_customer_audience'] as const;
export type AgentPlanToolName = (typeof AGENT_PLAN_TOOLS)[number];

export const AGENT_PLAN_TOOL_REGISTRY: Record<
  AgentPlanToolName,
  { kind: 'write'; minRole: 'MEMBER' | 'ADMIN'; operatorAllowed: boolean }
> = {
  /* Katalog yazma operasyonlarıyla AYNI eşik (`PLATFORM_OP_WRITE_ROLE`). */
  platform_op_plan: { kind: 'write', minRole: 'MEMBER', operatorAllowed: true },
  /* Müşteri kişisel verisini bir reklam platformuna aktarmak: REST'te
     hassas yazma ADMIN (`platform-ops.controller.ts`) — sohbette de ADMIN.
     Operatör kipinde KAPALI: arka plan AI'sı müşteri verisi taşımaz. */
  build_customer_audience: { kind: 'write', minRole: 'ADMIN', operatorAllowed: false },
};

/**
 * Onay kartının BAŞARISIZLIK sebebi (Faz 5) — `status === 'failed'` iken
 * dolu. `code` makine kodu (`OP_INVALID_PARAMS`, `PLATFORM_REJECTED`,
 * `PII_SEAL_EXPIRED`…); `message` sanitize edilmiş, platformun kendi cümlesini
 * taşıyabilen tek satır (çevrilmez). Arayüz koda göre kendi cümlesini seçip
 * mesajı ayrıntı olarak basıyor.
 */
export interface AgentProposalError {
  code: string;
  message: string;
  /**
   * Platform reddinin makine okunur sınıfı (Faz 8 E) — `platformRejection`
   * gövdesinin `details`i (`ACCESS_REQUIRED`, `RECONNECT_REQUIRED`…). Başarısız
   * kart "ne gerekiyor" bölümünü bundan kuruyor (`needsActionFromFailure`);
   * eski satırlarda yok.
   */
  rejection?: { category: string; providerCode?: string; retryAfterSeconds?: number } | null;
}

/**
 * Otomatik pilotun onayladığı satırın `approvedById` değeri.
 *
 * Kolon bir kullanıcı kimliği değil DÜZ METİN (yabancı anahtar yok) ve
 * "kim onayladı" sorusunun dürüst cevabı bu: bir insan değil, çalışma
 * alanının açıkça açtığı politika. Arayüz bu değeri "Otomatik pilot" diye
 * basıyor; bir kullanıcı adına çevirmeye çalışmıyor.
 */
export const AUTOPILOT_APPROVER_ID = 'autopilot' as const;

// ═══════════════════════════════════════════════════════════════════════════
// Öneri (onay kartı)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * 🔴 Önerinin ŞEMADA EVİ YOK.
 *
 * `WorkflowActionLog` → `WorkflowRun` → `Workflow` zinciri zorunlu; ajanın
 * ürettiği tek seferlik aksiyonun bağlanacağı bir `Workflow` satırı yok.
 *
 * Sözleşme kararı (a): workspace başına GİZLİ BİR SİSTEM WORKFLOW'U açılır —
 * `name = '__agent__'`, `enabled = false`, `scope.system = 'agent'`. Ajan
 * önerileri onun altında bir `WorkflowRun` + `WorkflowActionLog` olarak
 * yaşar. Şema değişikliği gerekmez ve onaylanan aksiyon otomasyonla AYNI
 * guardrail/idempotency/log yolundan geçer — ki §4.3'ün istediği tam bu.
 *
 * Bedeli: kural listesi ekranı bu satırı FİLTRELEMEK ZORUNDA
 * (`scope.system === 'agent'` olanlar gizlenir). Filtrelenmezse kullanıcı
 * kurmadığı bir kural görür.
 */
export const AGENT_SYSTEM_WORKFLOW_NAME = '__agent__';

/**
 * Kreatif onaylarının gizli sistem kuralı — Şema borç turu.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Neden AJANINKİ PAYLAŞILMADI
 * ═══════════════════════════════════════════════════════════════════════════
 * `AgentProposalService.listPending` bekleyen önerileri KURAL ADIYLA buluyor
 * (`workflow: { name: AGENT_SYSTEM_WORKFLOW_NAME }`). Kreatif onayları aynı
 * kuralın altına yazılsaydı ajanın öneri listesine sızarlardı ve orada
 * `inputMetrics.agent` taşımadıkları için `tool`u boş kartlar olarak
 * görünürlerdi. Ayrı ad, o sızıntıyı yapısal olarak imkânsız kılıyor —
 * ajanın hiçbir sorgusu değişmedi.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Neden BU DOSYADA — `moderation.ts`te değil
 * ═══════════════════════════════════════════════════════════════════════════
 * Aşağıdaki liste ikisini birlikte tutuyor ve KURAL LİSTESİ EKRANLARININ
 * TEK FİLTRESİ o. İki adı iki dosyaya bölmek, listeye eklemeyi unutmayı
 * mümkün kılardı — ve o unutmanın bedeli kullanıcının kurmadığı bir kuralı
 * ekranda görmesi. Adların yan yana durması, aralarındaki bağın tek görünen
 * hâli.
 */
export const CREATIVE_SYSTEM_WORKFLOW_NAME = '__creative__';

/**
 * Kullanıcı arayüzünde GİZLENECEK kural adları.
 *
 * 🔴 Yeni bir sistem kuralı açan her adım bu listeye girer. Girmezse kural
 * listesi, koşu listesi, onboarding sayacı ve ajan bağlamı kullanıcının
 * kurmadığı bir kuralı gösterir — dört ayrı ekranda, dördü de aynı sebeple.
 * `system-workflow.spec.ts` listenin bütün üyelerinin filtrelendiğini
 * doğruluyor.
 */
export const SYSTEM_WORKFLOW_NAMES: readonly string[] = [
  AGENT_SYSTEM_WORKFLOW_NAME,
  CREATIVE_SYSTEM_WORKFLOW_NAME,
];

export interface AgentProposal {
  /// = `WorkflowActionLog.id`
  proposalId: string;
  /// `platform_op` = katalog operasyonu; kartın içeriği `after.draft`
  /// (`AgentPlatformOpDraft`).
  tool: AgentProposalTool;
  /// Okunur özet: i18n anahtarı + parametre.
  summaryKey: string;
  params: Record<string, string | number>;
  /// 🔴 `PRODUCT` bir metrik seviyesi DEĞİL, aksiyonun HEDEFİ (Adım 3).
  /// `WorkflowActionLog.targetLevel` `MetricLevel` tipinde ve katalog
  /// düzeltmesinin hedefi bir ürün. Ayrı bir onay yolu açmak yasaktı, o
  /// yüzden hedef tipi genişledi — gerekçe `enums.ts`te.
  targetLevel: 'CAMPAIGN' | 'ADSET' | 'AD' | 'PRODUCT' | null;
  targetId: string | null;
  targetName: string | null;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  /// Guardrail ön kontrolü — öneri kartı "bu değişiklik tavanı aşıyor"
  /// diyebilsin; kullanıcı onaylayıp sonra reddedilmesin.
  guardrailCheck: {
    passed: boolean;
    /// Aşılan kural (`budget_percent`, `daily_limit`, `entitlement`).
    violations: string[];
  };
  expiresAt: string;
  /**
   * `superseded`: kullanıcı düzeltme yapınca ajan AYNI sohbette yeni bir öneri
   * üretti ve bu kart onun yerine geçti.
   *
   * `expired`den AYRI olmak zorunda: süresi dolan bir kart "geç kaldınız"
   * derken, yerine yenisi gelen kart "bu artık yanlış" diyor. İkisi tek kod
   * taşısaydı kullanıcı eskimiş bir kampanyayı süresi dolmuş sanıp yenisini
   * beklerdi — oysa yenisi zaten ekranda.
   */
  status:
    | 'pending'
    | 'approved'
    /**
     * İşlem planı ONAYLANDI ve arka planda adım adım uygulanıyor (Faz 5):
     * `after.draft.run` ilerlemeyi, `steps[].result` biten adımları taşıyor;
     * canlı konu `proposals` (`proposal:<id>`) her adımda haber veriyor.
     */
    | 'applying'
    | 'rejected'
    | 'expired'
    | 'superseded'
    | 'applied'
    | 'failed';
  /**
   * Neden UYGULANAMADI (Faz 5, 29 Eyl 2026) — yalnız `status === 'failed'`
   * iken dolu, aksi hâlde `null`. Kart "Uygulanamadı"nın yanına sebebi
   * yazabilsin. Plan kartında başarısız ADIMIN hatası (hangi adım:
   * `after.draft.failedStep`).
   *
   * Sunucu HER ZAMAN dolduruyor (`null` dahil); isteğe bağlı olması yalnız
   * elle kurulan eski örnek kartlar (arayüz laboratuvarı) kırılmasın diye.
   */
  error?: AgentProposalError | null;
  /**
   * Kartı karara bağlayabilecek en düşük rol (Faz 5) — sunucunun onay
   * kapısıyla AYNI hesap (`assertDecisionRole`): aracın rolü; operatör kartı,
   * geri alınamaz / hassas / kişisel veri taşıyan iş → ADMIN. Arayüz düğmeyi
   * bununla açıyor/kapatıyor.
   */
  decisionRole?: 'VIEWER' | 'MEMBER' | 'ADMIN' | 'OWNER';
  /**
   * Kartı İNSAN değil OTOMATİK PİLOT onayladı (29 Eyl 2026).
   *
   * Çalışma alanı `automationMode = AUTOPILOT` iken ve öneri politikanın
   * (`decideOp`) sınırları içindeyse sunucu kartı kendisi onaylıyor. Kart
   * "otomatik uygulandı" diyebilsin diye ayrı alan; `status` yine
   * `approved`/`applied`/`failed`.
   */
  autoApproved?: boolean;
  /**
   * Kartın DOĞDUĞU arka plan operatörü koşusu — sohbet kartında `null`.
   * Operatör kartları sohbetsiz; yöneticiler bekleyen listede görüyor.
   */
  operatorRunId?: string | null;
  /**
   * Kullanıcı kartı KENDİ düzenledi mi (19 Eyl 2026, `PATCH
   * /agent/proposals/:id`).
   *
   * Kart artık sayfanın formu: ajan doldurur, kullanıcı kartın içinde
   * değiştirir, sonra onaylar. Bu alan iki yere yarıyor — kart "senin
   * düzeltmen" diye işaret basabiliyor ve bir sonraki turda modele "kullanıcı
   * şu alanları değiştirdi" bilgisi gidiyor (`AGENT_CARDS_BLOCK_TYPE`).
   */
  editedFields: string[];
  /**
   * Kartın DOĞDUĞU sohbet.
   *
   * ═══════════════════════════════════════════════════════════════════════
   * Neden eklendi
   * ═══════════════════════════════════════════════════════════════════════
   * Kimlik `inputMetrics.agent.conversationId` içinde BAŞTAN BERİ duruyordu
   * ama dışarı verilmiyordu. Sonucu ölçüldü: `GET /agent/proposals` kartın
   * hangi sohbette üretildiğini söylemediği için bağlam paneli aynı kartın
   * İKİNCİ bir kopyasını çiziyordu — bir bileşen, iki yer.
   *
   * Alan gelince panel "şu sohbete git" satırına indi: kart tek yerde,
   * doğduğu konuşmanın içinde yaşıyor.
   */
  conversationId: string | null;
}

export const proposalDecisionSchema = z.object({
  decision: z.enum(['approve', 'reject']),
  note: z.string().trim().max(300).optional(),
  /**
   * Marka kimliği (Meta'da Sayfa kimliği) — yalnız `create_campaign` kartında.
   *
   * ═══════════════════════════════════════════════════════════════════════
   * 🔴 KARTA GELİYOR, PLANA DEĞİL — ve bu bilinçli
   * ═══════════════════════════════════════════════════════════════════════
   * Plan araç çağrısında doğuyor; modelin bu numarayı bilmesi MÜMKÜN DEĞİL
   * ve uydurması reklamın yanlış marka adına yayınlanması demek. Onaya basan
   * İNSAN biliyor — elle sihirbaz da aynı numarayı aynı yerden alıyor
   * (`identityRefStorageKey`, tarayıcıda hesap başına), yani kullanıcı onu
   * ikinci kez öğrenmiyor.
   *
   * Neden gerekli: ajan HER ZAMAN yeni bir ad set açıyor ve Meta yeni bir ad
   * set'in İLK reklamında sayfayı kardeş bir reklamdan devralamıyor
   * (`meta.connector.ts` → `resolvePageId` net bir hata fırlatıyor). Boş
   * gönderilirse zincir, kampanya ve ad set PLATFORMDA OLUŞTUKTAN SONRA
   * düşer ve geri alınamaz.
   *
   * Opsiyonel çünkü `reject` kararında ve kampanya dışı kartlarda anlamı yok;
   * zorunluluk uygulama katmanında, kampanya dalında.
   */
  identityRef: z.string().trim().min(1).max(128).optional(),
});
export type ProposalDecisionInput = z.infer<typeof proposalDecisionSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// Konuşma ve mesajlar
// ═══════════════════════════════════════════════════════════════════════════

export interface ConversationRow {
  id: string;
  title: string | null;
  createdAt: string;
  updatedAt: string;
  messageCount: number;
  /// Son mesajın ilk satırı — yan listede önizleme.
  preview: string | null;
  /**
   * Sohbeti HANGİ MOTOR sürüyor (22 Ağu 2026).
   *
   * 🔴 Liste satırının taşıması ZORUNLU: kullanıcı bir kampanya oturumuna
   * tıkladığında konsol onu GENEL ajan sohbeti olarak açarsa kart, şıklar ve
   * form kaybolur — kaydedilmiş bir konuşma açılıp bozuk görünür. Kip yalnız
   * sunucuda bilinseydi konsol her tıklamada bir istek daha atmak zorunda
   * kalırdı.
   */
  kind: 'GENERAL' | 'CAMPAIGN';
}

export const conversationListQuerySchema = listQuerySchema.extend({
  sort: z.enum(['updatedAt', 'createdAt']).default('updatedAt'),
});

export const conversationCreateSchema = z.object({
  title: z.string().trim().max(120).optional(),
  /// İlk mesaj — boş sohbet açmadan doğrudan soru sorulabilsin.
  message: z.string().trim().max(8000).optional(),
});
export type ConversationCreateInput = z.infer<typeof conversationCreateSchema>;

export const conversationRenameSchema = z.object({
  title: z.string().trim().min(1).max(120),
});

export interface AgentMessageRow {
  id: string;
  role: MessageRole;
  /// Anthropic content block dizisi olarak saklanıyor. Arayüz metin
  /// bloklarını basar, `tool_use` bloklarını AJ3 adımı olarak açar.
  content: unknown[];
  toolCalls:
    | {
        toolCallId: string;
        name: string;
        input: unknown;
        /// 🔴 `GET /agent/conversations/:id` yanıtında KIRPILIYOR — ~25 KB'lık
        /// ham gövde ekranda hiç basılmıyor. Gerekçe `AgentCard` başlığında.
        result?: unknown;
        ok?: boolean;
        /// Ekranın çizdiği dar zarf. Bu KIRPILMIYOR: kartın kalıcılığı buna bağlı.
        ui?: AgentCard | null;
      }[]
    | null;
  inputTokens: number | null;
  outputTokens: number | null;
  model: string | null;
  createdAt: string;
  /// Bu mesajda üretilmiş öneriler (onay kartları).
  proposals: AgentProposal[];
  /**
   * İSTEK SAHİBİNİN bu yanıta verdiği not — başkasınınki değil.
   *
   * Sohbeti başka bir üye de açabiliyor ve tablo `(messageId, userId)`
   * çiftinde benzersiz; burada dönen değer her zaman "ben ne demiştim".
   * Toplam sayılar yalnız `/admin/ai` tarafında.
   *
   * `null` iki şeyi birden anlatıyor: not verilmemiş ya da geri alınmış.
   * İkisini ayırmanın arayüzde bir karşılığı yok — düğmeler ikisinde de boş.
   */
  myRating: AgentFeedbackRating | null;
}

// ═══════════════════════════════════════════════════════════════════════════
// Yanıt notu — beğendim / beğenmedim
// ═══════════════════════════════════════════════════════════════════════════

/**
 * AJ14 — bir yanıtın işe yarayıp yaramadığı.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Neden üç değerli: UP · DOWN · null
 * ═══════════════════════════════════════════════════════════════════════════
 * Üçüncü değer GERİ ALMA. İki değerli bir alan (beğendi mi evet/hayır)
 * "hiç dokunmadım" ile "beğenmedim"i aynı kutuya atardı ve memnuniyet oranı
 * dokunulmamış her yanıtı olumsuz sayardı — panelde bu, ürün her gün kötüye
 * gidiyor gibi görünmek demekti.
 *
 * Aynı düğmeye ikinci kez basmak notu SİLİYOR; arayüz `null` gönderiyor.
 */
export const agentFeedbackRatingSchema = z.enum(['UP', 'DOWN']);
export type AgentFeedbackRating = z.infer<typeof agentFeedbackRatingSchema>;

/**
 * `POST /agent/messages/:id/feedback` gövdesi.
 *
 * `rating: null` bir hata değil, bir KOMUT: notu kaldır. `nullable()` +
 * zorunlu alan bilerek — alanın hiç gelmemesi ile açıkça `null` gelmesi
 * arasındaki farkı korumak istiyoruz, yoksa bozuk bir istemci notu sessizce
 * siler.
 */
export const agentFeedbackSetSchema = z.object({
  rating: agentFeedbackRatingSchema.nullable(),
});
export type AgentFeedbackSetInput = z.infer<typeof agentFeedbackSetSchema>;

/// Ucun yanıtı — kaydedilen son hâl. Arayüz iyimser yazıyor ama doğruyu
/// buradan okuyor (iki sekmede aynı mesaja not verilmiş olabilir).
export interface AgentFeedbackResponse {
  messageId: string;
  rating: AgentFeedbackRating | null;
}

/**
 * AJ1 / AJ11 — SSE ile streaming.
 *
 * İptal: istemci bağlantıyı kapatınca üretim durdurulur ve O ANA KADARKİ
 * metin mesaj olarak KAYDEDİLİR. Yarım yanıtı çöpe atmak, kullanıcının
 * gördüğü şeyi geçmişte bulamaması demek.
 */
/**
 * Mesaja iliştirilen görseller — AJ13.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Neden yalnız GÖRSEL, neden bu üç mime
 * ═══════════════════════════════════════════════════════════════════════════
 * Sağlayıcı `image` bloğu olarak JPEG / PNG / GIF / WebP kabul ediyor; video
 * hiç kabul etmiyor. Yükleme ucunun beyaz listesi (`UPLOAD_MIME_ALLOWLIST`)
 * mp4'ü de içeriyor çünkü orada asıl iş kreatif üretimi — burada aynı listeyi
 * kullanmak, ajana gönderilemeyecek bir dosyayı yüklettirmek olurdu.
 *
 * GIF dışarıda: yalnız ilk kare okunuyor ve kullanıcı hareketli bir görsel
 * gönderip "bunu izle" diyemez; sessizce ilk kareyi analiz etmek yanlış cevaba
 * yol açar.
 *
 * ---------------------------------------------------------------------------
 * Sınırlar NEREDEN geliyor
 * ---------------------------------------------------------------------------
 * Sağlayıcı sınırı görsel başına 10 MB (base64 hâliyle) ve istek gövdesi
 * toplamda 32 MB. Base64 ham baytı ~%33 şişiriyor, yani 5 MB'lık bir dosya
 * ~6,7 MB olarak gidiyor: dört tanesi 27 MB, gövde sınırının altında.
 * Bu yüzden tavan 5 MB ve mesaj başına 4 görsel — ikisi birlikte seçildi,
 * biri değiştirilecekse diğeri de hesaplanmalı.
 */
export const AGENT_ATTACHMENT_MIME = ['image/jpeg', 'image/png', 'image/webp'] as const;
export const AGENT_ATTACHMENT_LIMITS = {
  maxPerMessage: 4,
  /// Ham dosya baytı; base64 sonrası ~1,33 katı.
  maxBytes: 5 * 1024 * 1024,
  /// Video — modele GİTMİYOR (izleyemez); kütüphaneye alınabilsin diye depoda.
  videoMaxBytes: 200 * 1024 * 1024,
  /// PDF — yalnız eklendiği turda belge bloğu olarak gidiyor (gerekçe aşağıda).
  pdfMaxBytes: 10 * 1024 * 1024,
  /// Müşteri listesi CSV'si — depoya DEĞİL şifreli kasaya (`POST /agent/customer-lists`).
  /// 4 MB: metin JSON gövdesinde gidiyor ve tırnak/satır kaçışı en kötü
  /// durumda boyutu ikiye katlıyor — API gövde tavanı 10 MB. 50 bin satır
  /// tipik bir listede ~3 MB.
  csvMaxBytes: 4 * 1024 * 1024,
  /// VERİ TABLOSU (Excel / veri CSV'si, Faz 9 X1) — modele GİTMEZ; kod
  /// çalıştırma konteynerine (`container_upload`) verilir, AI kodla okur.
  sheetMaxBytes: 10 * 1024 * 1024,
} as const;

/**
 * EK → ARAÇ KÖPRÜSÜ (Faz 8, 30 Eyl 2026 — kreatif ajanı).
 *
 * Denetimin 1. bulgusu: ek modele GÖRSEL olarak gidiyordu ama hiçbir araç ona
 * başvuramıyordu — "şu fotoğraftan reklam yap", "bu logoyu kaydet", "bu videoyu
 * reklamda kullan" çıkmazdı. Artık her ekin sohbet içinde SABİT bir kimliği
 * var (`a1`, `a2`… — sunucu verir, satırda saklanır) ve model her ekin önünde
 * `[Attachment a1: image/png "urun.jpg" …]` satırını görüyor; araçlar
 * (`generate_creative` `sourceAttachmentId`/`logoAttachmentId`,
 * `creative_import`, `brand_logo_add`, `studio_model_from_photo`,
 * `build_customer_audience` `source: file`) kimliği SUNUCUDA anahtara çeviriyor
 * — model depo anahtarı görmüyor, yazmıyor.
 *
 * Türler ve modele gidişleri:
 *   görsel (JPEG/PNG/WebP) → görsel bloğu (bugünkü gibi) · istemci 5 MB'a
 *     küçültüyor, SVG'yi PNG'ye, HEIC'i (tarayıcı çözebiliyorsa) JPEG'e çeviriyor
 *   video (MP4/MOV/WebM)   → yalnız satır: model izleyemez, kütüphaneye alabilir
 *   PDF                    → YALNIZ EKLENDİĞİ TURDA belge bloğu; sonraki
 *     turlarda tek satır (sayfa başına binlerce jeton, her turda yeniden
 *     gönderilmesin; 100 sayfa sınırını aşan bir PDF de sohbeti kalıcı kırmasın)
 *   CSV (müşteri listesi)  → depoya HİÇ gitmiyor: şifreli, süreli kasada
 *     (`listRef`); modele yalnız satır sayısı ve tanınan kolonlar
 */
export const AGENT_ATTACHMENT_VIDEO_MIME = ['video/mp4', 'video/quicktime', 'video/webm'] as const;
export const AGENT_ATTACHMENT_DOC_MIME = ['application/pdf'] as const;
export const AGENT_ATTACHMENT_LIST_MIME = ['text/csv'] as const;
/**
 * VERİ TABLOSU (Faz 9 X1, 1 Eki 2026) — Excel. CSV de veri tablosu olabilir:
 * `text/csv` + `assetKey` (depoda) = veri tablosu, `text/csv` + `listRef`
 * (şifreli kasa) = müşteri listesi (`agentAttachmentKindOf`). Tarayıcı CSV'nin
 * başlığına bakıp kişi kolonu (e-posta/telefon/ad) varsa kasaya gönderiyor;
 * sunucu konteynere vermeden ÖNCE dosyayı yeniden tarıyor (ikinci kemer).
 */
export const AGENT_ATTACHMENT_SHEET_MIME = [
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
] as const;
export const AGENT_ATTACHMENT_ALL_MIME = [
  ...AGENT_ATTACHMENT_MIME,
  ...AGENT_ATTACHMENT_VIDEO_MIME,
  ...AGENT_ATTACHMENT_DOC_MIME,
  ...AGENT_ATTACHMENT_LIST_MIME,
  ...AGENT_ATTACHMENT_SHEET_MIME,
] as const;
export type AgentAttachmentMime = (typeof AGENT_ATTACHMENT_ALL_MIME)[number];

/** Ekin türü — mime'dan türetiliyor (istemci ayrıca söylemiyor). */
export type AgentAttachmentKind = 'image' | 'video' | 'pdf' | 'customer_list' | 'spreadsheet';
export function agentAttachmentKind(mime: string): AgentAttachmentKind | null {
  if ((AGENT_ATTACHMENT_MIME as readonly string[]).includes(mime)) return 'image';
  if ((AGENT_ATTACHMENT_VIDEO_MIME as readonly string[]).includes(mime)) return 'video';
  if ((AGENT_ATTACHMENT_DOC_MIME as readonly string[]).includes(mime)) return 'pdf';
  if ((AGENT_ATTACHMENT_LIST_MIME as readonly string[]).includes(mime)) return 'customer_list';
  if ((AGENT_ATTACHMENT_SHEET_MIME as readonly string[]).includes(mime)) return 'spreadsheet';
  return null;
}

/**
 * Ekin türü — CSV'de NEREDE durduğuna göre: kasada (`listRef`) müşteri
 * listesi, depoda (`assetKey`) veri tablosu. Diğer türlerde `agentAttachmentKind`.
 */
export function agentAttachmentKindOf(input: { mimeType: string; listRef?: string | null; assetKey?: string | null }): AgentAttachmentKind | null {
  const kind = agentAttachmentKind(input.mimeType);
  if (kind === 'customer_list' && !input.listRef && input.assetKey) return 'spreadsheet';
  return kind;
}

/** Türün bayt tavanı (istemci ve sunucu aynı tablodan). */
export function agentAttachmentMaxBytes(kind: AgentAttachmentKind): number {
  if (kind === 'video') return AGENT_ATTACHMENT_LIMITS.videoMaxBytes;
  if (kind === 'pdf') return AGENT_ATTACHMENT_LIMITS.pdfMaxBytes;
  if (kind === 'customer_list') return AGENT_ATTACHMENT_LIMITS.csvMaxBytes;
  if (kind === 'spreadsheet') return AGENT_ATTACHMENT_LIMITS.sheetMaxBytes;
  return AGENT_ATTACHMENT_LIMITS.maxBytes;
}

/** Sohbet içi ek kimliği — `a1`, `a2`… (sunucu verir). */
export const AGENT_ATTACHMENT_ID = /^a[1-9]\d{0,3}$/;
export const agentAttachmentIdSchema = z
  .string()
  .trim()
  .regex(AGENT_ATTACHMENT_ID, 'An attachment id from the conversation, like "a1".');

export const messageAttachmentSchema = z
  .object({
    /// `POST /creatives/uploads` biletinden dönen S3 anahtarı. Sunucu bunun BU
    /// çalışma alanına ait olduğunu doğrular — istemciden geldiği için
    /// güvenilmez (`isOwnObjectKey`). Müşteri listesinde YOK (`listRef`).
    assetKey: z.string().min(1).max(400).optional(),
    /// Müşteri listesi kasasının referansı (`POST /agent/customer-lists`) —
    /// sunucu bu çalışma alanına ve süresinin dolmadığına bakıyor.
    listRef: z.uuid().optional(),
    mimeType: z.enum(AGENT_ATTACHMENT_ALL_MIME),
    /// Yalnız arayüzde gösterilir; modele tırnak içinde, veri olarak gider.
    fileName: z.string().trim().max(200).optional(),
    /// Ham bayt — arayüz çipi ve modele giden satır için (yalnız bilgi).
    sizeBytes: z.number().int().min(1).max(AGENT_ATTACHMENT_LIMITS.videoMaxBytes).optional(),
    /// Ek bir DOSYA İSTEĞİ kartından geldiyse o kartın `toolCallId`si — kart
    /// "yüklendi" hâline geçiyor (`upload` kartı).
    viaCard: z.string().min(1).max(120).optional(),
  })
  .superRefine((value, ctx) => {
    const kind = agentAttachmentKind(value.mimeType);
    if (kind === 'customer_list') {
      /* CSV: kasada (`listRef`) müşteri listesi YA DA depoda (`assetKey`)
         veri tablosu — ikisi birden değil (Faz 9 X1). */
      if (Boolean(value.listRef) === Boolean(value.assetKey)) {
        ctx.addIssue({ code: 'custom', path: ['listRef'], message: 'A CSV needs listRef (customer list) or assetKey (data table), not both.' });
      }
    } else if (!value.assetKey || value.listRef) {
      ctx.addIssue({ code: 'custom', path: ['assetKey'], message: 'This attachment needs assetKey.' });
    }
  });
export type MessageAttachmentInput = z.infer<typeof messageAttachmentSchema>;

/**
 * MÜŞTERİ LİSTESİ YÜKLEMESİ — `POST /agent/customer-lists` (Faz 8).
 *
 * 🔴 KİŞİSEL VERİ. CSV depoya (R2) GİTMİYOR: sunucu satırları okuyup yalnız
 * reklam kitlesinin kullandığı alanları (e-posta, telefon, ad, soyad, ülke,
 * posta kodu, şehir) `TOKEN_VAULT_KEY` ile şifreleyip SÜRELİ bir kasaya
 * koyuyor (kişisel veri mührüyle aynı kasa, `pii-seal.ts`); diğer kolonlar
 * hiç saklanmıyor. Yanıt ve sohbet satırı yalnız referans + sayılar taşır.
 */
export const CUSTOMER_LIST_LIMITS = {
  /// Tek listede en fazla satır — kitle aracının tavanı (`CUSTOMER_AUDIENCE_LIMITS.maxPeople`).
  maxRows: 50_000,
  /// Kasada kalma süresi — kitle kartı kendi mührünü ayrıca taşıyor.
  ttlHours: 24,
} as const;

export const customerListUploadSchema = z
  .object({
    fileName: z.string().trim().min(1).max(200),
    /// CSV metni (UTF-8). Gövde tavanı API'de 10 MB; liste 4 MB.
    csv: z.string().min(1).max(AGENT_ATTACHMENT_LIMITS.csvMaxBytes),
  })
  .strict();
export type CustomerListUploadInput = z.infer<typeof customerListUploadSchema>;

/** Tanınan kolon — kitle aracının alanları. */
export const CUSTOMER_LIST_FIELDS = [
  'email',
  'phone',
  'firstName',
  'lastName',
  'countryCode',
  'postalCode',
  'city',
] as const;
export type CustomerListField = (typeof CUSTOMER_LIST_FIELDS)[number];

export interface CustomerListUploadResponse {
  listRef: string;
  /// Okunan veri satırı (başlık hariç).
  rows: number;
  withEmail: number;
  withPhone: number;
  /// Tanınan alanlar — kolon ADLARI değil, eşlendikleri alan.
  fields: CustomerListField[];
  /// Tanınmayan (saklanmayan) kolon sayısı.
  ignoredColumns: number;
  /// Satır tavanını aştı mı (fazlası okunmadı).
  truncated: boolean;
  expiresAt: string;
}

/* ══════════════════════════════════════════════════════════════════════════
   EKRAN KARTLARI — araç sonucunun DOKUNULABİLİR yüzü
   ══════════════════════════════════════════════════════════════════════════ */

/*
 * `AgentCard` birliği Faz 8'de (30 Eyl 2026) alan dosyalarına bölündü:
 * `./agent-cards/` (çekirdek + analiz + kampanya + kreatif + operasyon, kart
 * eylemi sözleşmesi). Tarif `./agent-cards/index.ts` başında; barrel oradan
 * dışa veriyor (`AgentCard`, `AGENT_TABLE_IDS`, `AgentTableFormat`…).
 */

/**
 * Karta verilen cevap.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * İKİ DAL ve aradaki fark ÖLÇÜLMÜŞ bir hatadan geliyor
 * ═══════════════════════════════════════════════════════════════════════════
 * `ask` dalında `value` DÜZ METİN ve kullanıcı balonu olarak basılıyor —
 * şıkkın metni cevabın kendisi. Değer taşıyan bir şık, konuşma geçmişinde
 * koduyla görünür ve model bir sonraki turda okuyamaz.
 *
 * Seçici dallarında (ürün/hesap/kreatif — Faz 3/4) `value` bir KİMLİK olacak
 * ve balon basılmayacak: model kırpılmış bir liste görüyor, arayüz tamamını
 * gösteriyor ve kullanıcı modelin göremediği bir satırı seçebiliyor. O alan
 * arayüzün, modelin değil.
 */
export const cardReplySchema = z.discriminatedUnion('kind', [
  z.object({
    toolCallId: z.string().min(1).max(120),
    kind: z.literal('ask'),
    /// Şıkkın METNİ — cevabın kendisi.
    value: z.string().trim().min(1).max(400),
  }),
  z.object({
    toolCallId: z.string().min(1).max(120),
    kind: z.literal('product'),
    /// Ürün KİMLİĞİ. Balon basılmıyor; kart "seçildi" hâline geçiyor.
    value: z.uuid(),
  }),
  z.object({
    toolCallId: z.string().min(1).max(120),
    kind: z.literal('creative'),
    /**
     * ÇOKLU: her kreatif bir reklam.
     *
     * Tavan `WIZARD_MAX_CREATIVES`ten OKUNUYOR, elle yazılmıyor. İkisi bir
     * dönem tesadüfen 10'du: kartın seçicisi o sabiti kullanıyor, bu şema ise
     * kendi 10'unu taşıyordu. Biri değiştiğinde kullanıcı seçebildiği bir
     * kreatif kümesiyle 400 alırdı.
     */
    values: z.array(z.uuid()).min(1).max(WIZARD_MAX_CREATIVES),
  }),
  /**
   * BAĞLANTI TAMAMLANDI — bağlantı kartından (30 Eyl 2026).
   *
   * 🔴 DEĞER TAŞIMIYOR. İstemci "bağlandım" DEMİYOR, "yeniden bak" diyor:
   * sunucu kartın platformunu KAYITTAN okuyor, durumu veritabanından yeniden
   * türetiyor, karta yazıyor ve modele o doğrulanmış durumu bir olay satırı
   * olarak veriyor (`mentorivo_pick`, balon basılmıyor). Ajan böylece
   * kullanıcı adına sahte bir mesaj uydurulmadan kendiliğinden devam ediyor.
   */
  z.object({
    toolCallId: z.string().min(1).max(120),
    kind: z.literal('connect'),
  }),
  /**
   * KART EYLEMİ OLAYI — kart düğmesinden (Faz 8, 30 Eyl 2026).
   *
   * 🔴 DEĞER TAŞIMIYOR — bağlantı kartıyla aynı gerekçe. Eylem ZATEN
   * yürüdü (`POST /agent/cards/:callId/actions/:actionId`) ve sonucu kartın
   * kaydında; bu dal yalnız "modele söyle" der. Sunucu o kartta modele henüz
   * ulaşmamış bir sonuç arar (yoksa `CARD_NOT_FOUND`, hak YANMAZ), olay
   * satırlarını KAYITTAN kurar (`[Card action — … not typed by the user]`) ve
   * turu açar. Balon basılmıyor.
   */
  z.object({
    toolCallId: z.string().min(1).max(120),
    kind: z.literal('action'),
  }),
  /**
   * ONAY OLAYI — onay kartı karara bağlandı ya da uygulaması bitti (Faz 8
   * dalga 2, 30 Eyl 2026). Onay kartları (`AgentProposal`) kart birliğinin
   * DIŞINDA ve bir `toolCallId`ye değil öneri satırına bağlı; bu yüzden
   * ayrı dal.
   *
   * 🔴 DEĞER TAŞIMIYOR — `connect` / `action` ile aynı gerekçe. İstemci "karar
   * verildi / iş bitti" DEMİYOR, "bak" diyor: sunucu bu sohbette modele henüz
   * ulaşmamış bir karar/sonuç arar (`AgentProposalService.hasApprovalEvent`;
   * yoksa `CARD_NOT_FOUND`, hak YANMAZ), olay satırlarını KAYITTAN kurar
   * (`[Approval update — … not typed by the user]`) ve turu açar — ajan sonucu
   * söyleyip zincirdeki sonraki adıma geçer. Kullanıcı adına sahte mesaj yok,
   * balon basılmıyor. `proposalId` yalnız kapsam denetimi (kart bu sohbetin
   * mi); tur o sohbetin BEKLEYEN BÜTÜN onay olaylarını birlikte taşır.
   */
  z.object({
    kind: z.literal('approval'),
    proposalId: z.uuid(),
  }),
]);
export type CardReplyInput = z.infer<typeof cardReplySchema>;

/**
 * SEÇİMİN MESAJDA saklanan hâli.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Neden bir metin bloğu DEĞİL
 * ═══════════════════════════════════════════════════════════════════════════
 * Seçim modele ULAŞMAK ZORUNDA — yoksa model kullanıcının ne seçtiğini
 * bilmiyor ve bir sonraki turda yeniden soruyor. Ama ekranda BALON olarak
 * görünmemeli: kartın kendisi "seçildi" hâline geçiyor ve altına bir de
 * kimlik yazan bir baloncuk koymak aynı bilgiyi iki kez göstermek olurdu.
 *
 * `mentorivo_intent` ile birebir aynı desen: blok satırda duruyor, arayüz
 * onu atlıyor, `replayContent` modele giderken tek satırlık metne çeviriyor.
 */
export const AGENT_PICK_BLOCK_TYPE = 'mentorivo_pick';

/**
 * KARTLARDAKİ DEĞİŞİMİN MESAJDA saklanan hâli (19 Eyl 2026).
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Neden gerekli
 * ═══════════════════════════════════════════════════════════════════════════
 * Model bir kart üretiyor ve sonra ne olduğunu HİÇ öğrenmiyordu: kullanıcı
 * onayladı mı, reddetti mi, bütçeyi kartın içinde 200'den 150'ye mi çekti.
 * Kayıtta ölçüldü (25 Ağu 2026): ajan "kampanya oluşmuş — yani kartı
 * onaylamışsın" diye TAHMİN ediyordu, çünkü bunu yalnız `get_campaigns`
 * sonucundan çıkarabiliyordu. Kart artık düzenlenebilir olduğuna göre bu boşluk
 * yanlış rakam söyleyen bir ajan demek.
 *
 * Kullanıcının bir sonraki mesajına, o mesajdan ÖNCE değişen kartların özeti
 * bu blokla ekleniyor. `mentorivo_pick` ile aynı desen: satırda duruyor,
 * arayüz onu atlıyor, `replayContent` modele giderken düz metne çeviriyor.
 * Sistem prompt'una YAZILMIYOR — orası önbellekli.
 */
export const AGENT_CARDS_BLOCK_TYPE = 'mentorivo_cards';

/**
 * KART DÜĞMESİ OLAYLARININ MESAJDA saklanan hâli (Faz 8, 30 Eyl 2026).
 *
 * Kullanıcı bir kart düğmesine bastı (`POST /agent/cards/:callId/actions/…`)
 * ve sonuç modele henüz ulaşmadı. Olay turu (`cardReply: action`) ya da
 * kullanıcının bir sonraki mesajı (`followUp: next`) o sonuçları bu blokla
 * taşıyor: YAPILANDIRILMIŞ kayıt (kart türü, eylem, durum, kod, özet) —
 * `replayContent` modele giderken `[Card action — … not typed by the user]`
 * satırlarına çeviriyor, arayüz atlıyor. `mentorivo_pick` ile aynı desen.
 */
export const AGENT_CARD_EVENTS_BLOCK_TYPE = 'mentorivo_card_events';

/**
 * ÇALIŞMA ALANI ANLIK GÖRÜNTÜSÜNÜN MESAJDA saklanan hâli (29 Eyl 2026).
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Neden sistem prompt'unda DEĞİL
 * ═══════════════════════════════════════════════════════════════════════════
 * "Bugün", bağlı hesaplar, mağazalar, kurallar ve operatör notları bir sohbet
 * SÜRERKEN değişebiliyor. Sistem prompt'u değişirse iki şey birden kırılıyor:
 * istem önbelleği (araçlar + sistem öneki her istekte yeniden yazılır) ve
 * Opus 5.5'in KORUNAN DÜŞÜNME'si — düşünme bloğunun imzası sistem prompt'unu
 * da bağlıyor, değişince önceki bütün düşünce düşüyor.
 *
 * Bu yüzden sistem prompt'u STATİK (rol + kurallar) ve anlık görüntü, DEĞİŞTİĞİ
 * turda kullanıcı mesajına bu blokla ekleniyor: geçmiş yalnız sona EKLENİYOR,
 * hiçbir önceki bayt değişmiyor. `mentorivo_cards` ile aynı desen: satırda
 * duruyor, arayüz atlıyor, `replayContent` modele giderken metne çeviriyor.
 */
export const AGENT_CONTEXT_BLOCK_TYPE = 'mentorivo_context';

/**
 * Tur başına EN FAZLA BİR SORU.
 *
 * İki soru sormak 22 Ağu 2026'da somut bir arızaya yol açtı: kullanıcı aynı
 * anda iki farklı "sonraki adım" görüyor ve hangisine cevap vereceğini
 * bilemiyordu. Sınır sözleşmede, ihlal sunucuda kırpılıyor.
 */
export const AGENT_ASK_LIMITS = { maxPerTurn: 1, maxOptions: 4 } as const;

/**
 * NİYET — modül seçicinin yerini alan şey (tek AI yüzeyi, Faz 1).
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Seçim bir KİP DEĞİL, bir İPUCU
 * ═══════════════════════════════════════════════════════════════════════════
 * Eski `AgentToolMode` konsolun mesaj sütununu ayrı bir motora devrediyordu:
 * seçmek ekran değiştirmekti ve seçmeden aynı iş yapılamıyordu. Kullanıcının
 * söylediği şey netti — *"sadece AI'ye haber verecek, farklı bir yerde konuşma
 * başlatmayacak"*.
 *
 * Niyet o yüzden mesajın METNİNE değil YANINA yazılıyor ve tek yaptığı şey
 * modele bir cümle iletmek. Motor değişmiyor, uç değişmiyor, sohbet açılmıyor.
 *
 * 🔴 HİÇBİR ZAMAN ZORUNLU DEĞİL. Kullanıcı düz metinle "kampanya oluştur"
 * yazdığında model aynı araç ailesini çağırıyor — araçlar araç defterinde ve
 * seçime bağlı değil. Niyet yalnız belirsizliği azaltıyor.
 *
 * Metne önek gömmek (`/kampanya …`) REDDEDİLDİ: API tek dilli, yani önek bir
 * dilde yanlış olurdu; ve `agent-composer.tsx` → `mention()` aynı kararı zaten
 * vermiş: *"gizli bir kimlik eklemek, kullanıcının okuduğu cümleyle gönderilen
 * cümleyi ayırırdı."*
 */
export const AGENT_INTENTS = ['campaign', 'creative', 'catalog', 'automation'] as const;
export type AgentIntent = (typeof AGENT_INTENTS)[number];

/**
 * Niyetin MESAJDA saklanan hâli.
 *
 * `mentorivo_image` ve `mentorivo_meta` ile aynı desen: Anthropic content-block
 * dizisinde yaşayan ama sağlayıcıya HAM GİTMEYEN bir blok. `replayContent` onu
 * modele giderken tek satırlık bir metne çeviriyor; arayüz ham hâlini okuyup
 * kullanıcı balonunun üstüne sessiz bir etiket basıyor.
 *
 * Sistem prompt'una YAZILMIYOR ve bu bir performans kararı: prompt öneki artık
 * `cache_control` ile önbellekli (`anthropic.llm.provider.ts`) ve her mesajda
 * değişen bir satır o önbelleği her istekte çürütürdü. Niyet zaten tek bir
 * mesaja ait; evi de o mesaj.
 */
export const AGENT_INTENT_BLOCK_TYPE = 'mentorivo_intent';

export const messageSendSchema = z
  .object({
    /**
     * 🔴 `min(1)` YOK — görsel tek başına gönderilebilmeli.
     *
     * "Şu ekran görüntüsüne bak" demek için kullanıcıyı ayrıca bir şey yazmaya
     * zorlamak yapay bir engel olurdu. Boşluk kontrolü aşağıdaki `refine`da:
     * metin de ek de yoksa gönderilecek bir şey yok.
     */
    message: z.string().trim().max(8000),
    /// Arayüz aynı mesajı iki kez göndermesin diye; `Idempotency-Key` ile aynı
    /// işi yapıyor ama sohbete özel ve zorunlu.
    clientMessageId: z.uuid(),
    attachments: z
      .array(messageAttachmentSchema)
      .max(AGENT_ATTACHMENT_LIMITS.maxPerMessage)
      .optional(),
    /// Yetenek şeridinden ya da composer çipinden gelen niyet. Tek TURLUK.
    /// 4 Eki 2026'dan beri arayüz göndermiyor (yerini `flow` aldı); eski
    /// istemciler ve API anahtarı için sözleşmede duruyor.
    intent: z.enum(AGENT_INTENTS).optional(),
    /**
     * HIZLI AKIŞ (4 Eki 2026) — yazma kutusunda açılan formun cevapları.
     *
     * Metin ve ek gibi mesajın bir parçası, kart cevabı gibi bir yerine geçme
     * DEĞİL. Sunucu `checkAgentFlow` ile denetleyip `mentorivo_flow` bloğu
     * olarak saklıyor; modele giderken reçete metnine dönüşüyor
     * (`@mentorivo/shared/agent-flow-recipes`). Gerekçe `agent-flows.ts`.
     */
    flow: agentFlowRequestSchema.optional(),
    /**
     * Bir KARTA verilen cevap — düz mesajın yerine geçiyor.
     *
     * Sunucu bunu, bekleyen `tool_result`u TAMAMLAYAN bir tur olarak işliyor:
     * model kendi sorduğu soruya cevabı aynı araç çağrısının sonucu olarak
     * görüyor ve konuşma kaldığı yerden sürüyor. Ayrı bir uç açmak, aynı
     * döngüyü ikinci kez yazmak olurdu.
     */
    cardReply: cardReplySchema.optional(),
    /**
     * Kullanıcının ARAYÜZ dili (19 Eyl 2026).
     *
     * Model mesajın diliyle cevap veriyor; ama "asfkaskf" gibi dili olmayan
     * bir mesaja İngilizce dönüyordu — Türkçe paneldeki kullanıcıya. Bu alan
     * yalnız o durumun yedeği; mesajın kendi dili her zaman önce gelir.
     * (29 Eyl 2026: 13 dil — `localeSchema`.)
     */
    locale: localeSchema.optional(),
  })
  .refine(
    (value) =>
      value.message.length > 0 ||
      (value.attachments?.length ?? 0) > 0 ||
      value.cardReply !== undefined ||
      value.flow !== undefined,
    { message: 'Bir şey yazın ya da görsel ekleyin', path: ['message'] },
  );
export type MessageSendInput = z.infer<typeof messageSendSchema>;

/// SSE olay adları — arayüz bunlara abone olur.
export const AGENT_SSE_EVENT = {
  DELTA: 'delta',
  TOOL_USE: 'tool_use',
  TOOL_RESULT: 'tool_result',
  /**
   * Araç çağrıları arasındaki ilerleme notu — `{ text, block }` (29 Eyl 2026).
   *
   * Cevap METNİ DEĞİL (`delta` o): Opus 5.5 araçlar arasındaki kısa
   * notlarını düşünme bloğunda döndürüyor. Aynı `block` numarasındaki parçalar
   * tek bir nottur. Arayüz bilmiyorsa yok sayabilir; hiçbir veri kaybolmaz.
   */
  PROGRESS: 'progress',
  PROPOSAL: 'proposal',
  USAGE: 'usage',
  /**
   * Sohbete otomatik konan ad — `{ title }`. Yalnız başlık GERÇEKTEN
   * değiştiğinde ve turda en fazla bir kez; `done`dan önce gelir.
   */
  TITLE: 'title',
  /**
   * Cevabın web kaynakları — `{ sources: AgentWebSource[] }` (1 Eki 2026).
   * Her asistan satırı kaydedildiğinde, kaynağı varsa; ekran tur içinde
   * birleştirir (tekrarsız). Geçmişte aynı liste satırın kaynak bloğunda.
   */
  SOURCES: 'sources',
  /**
   * Adsız canlı durum — `{ activity: 'researching' }` (1 Eki 2026). Araç adı
   * yok; ekran "Düşünüyor" yerine "Araştırıyor" yazar.
   */
  STATUS: 'status',
  /**
   * CANLI ETKİNLİK PANELİ (1 Eki 2026) — `AgentActivityFrame` (`agent-activity.ts`):
   * `start` (turun İLK karesi, sağlayıcı beklenmeden), `step` (okunur adım:
   * i18n anahtarı + parametre — ham araç adı YOK), `note` (düşünme notu,
   * kişisel veri ayıklanmış), `end`. Bilmeyen istemci yok sayar.
   */
  ACTIVITY: 'activity',
  DONE: 'done',
  ERROR: 'error',
} as const;

// ═══════════════════════════════════════════════════════════════════════════
// Bağlam, öneriler, limit
// ═══════════════════════════════════════════════════════════════════════════

/**
 * AJ12 — ajanın ne gördüğü. Aynı yapı sistem prompt'una da girer.
 *
 * JETON, SIR ve e-posta adresi GİRMEZ. Modelin çıktısı kullanıcıya gidiyor;
 * prompt'a koyduğunuz her şey bir gün ekranda görünebilir.
 */
export interface AgentContextResponse {
  workspace: { name: string; timezone: string; currency: string; today: string };
  /**
   * Reklam hesapları.
   *
   * 🔴 `id` sistem prompt'una da giriyor ve bu bir gereklilik: kampanya kuran
   * araç `adAccountId` istiyor ve o kimlik BİZİM `AdAccount.id`imiz, platformun
   * değil. Prompt'ta olmasaydı model her kampanya turunda önce
   * `get_connections` çağırmak zorunda kalırdı — bir araç çağrısı, bir gecikme
   * ve tur başına sekizlik bütçeden bir kalem.
   */
  connections: { id: string; platform: string; name: string; currency: string; health: string }[];
  /**
   * Bağlı MAĞAZALAR — reklam hesaplarından ayrı (Faz 1).
   *
   * Ürün kataloğunun kaynağı bunlar ve `list_products` bir `platform` süzgeci
   * taşıyor. Model hangi platformların bağlı olduğunu bilmeden o süzgece
   * bağlı olmayan bir değer yazabilir ve boş bir liste alıp "kataloğunuz boş"
   * diye yanlış cevap verebilirdi.
   *
   * Kimlik TAŞIMIYOR: hiçbir araç mağaza bağlantısı kimliği istemiyor ve
   * prompt'a giren her alanın bir tüketicisi olmalı.
   */
  stores: { platform: string; name: string; currency: string }[];
  activeWorkflows: { id: string; name: string; mode: string }[];
  plan: { tier: string; adSpendLimit: number; maxAdAccounts: number; maxMembers: number };
  credits: { available: number; reserved: number };
  /// AJ10 — kalan hak.
  dailyMessages: { used: number; limit: number; resetsAt: string };
  /// Sağlayıcı hazır mı; değilse arayüz sohbeti kilitler.
  llmConfigured: boolean;
  model: string;
  /// Hangi araçlar bu kullanıcı için açık (rol + bayrak).
  availableTools: AgentToolName[];
}

/// AJ8 — boş sohbette gösterilecek örnek sorular. i18n anahtarı + parametre
/// döner, DÜZ METİN DEĞİL: iki dilli üründe sabit metin bir dilde yanlış olur.
export interface AgentSuggestion {
  key: string;
  params?: Record<string, string | number>;
  /// Hangi duruma özel: hesap yoksa "önce hesap bağla".
  condition: 'no_connection' | 'no_data' | 'has_data' | 'has_workflows';
}

// ═══════════════════════════════════════════════════════════════════════════
// Dürüstlük ve kötüye kullanım
// ═══════════════════════════════════════════════════════════════════════════

/**
 * AJ9 — DÜRÜSTLÜK KURALI.
 *
 * Tool hata verirse hata kullanıcıya AYNEN iletilir; tool sonucu olmadan
 * metrik ÜRETİLMEZ. Sistem prompt'unda yazılı olması yetmez — model onu ihlal
 * edebilir ve ihlal ettiğinde kimse fark etmez.
 *
 * Sunucu tarafı işaret: yanıt akışında hiç `tool_result` YOKKEN sayısal iddia
 * içeren bir yanıt üretilirse mesaj `unverifiedNumbers: true` ile
 * işaretlenir ve arayüz uyarı rozeti basar. Test listesine "API kapalıyken
 * ajana metrik sor" adımı girer.
 */
export const AGENT_LIMITS = {
  /// Tek mesajın en fazla karakteri.
  maxMessageChars: 8000,
  /**
   * Bir konuşmadaki en fazla SATIR (kullanıcı + asistan + araç sonucu);
   * aşılınca yeni sohbet önerilir.
   *
   * 29 Eyl 2026: 200 → 600. Sayaç araç sonucu satırlarını da sayıyor ve
   * sohbetten kampanya kurmak tek turda 20+ araç çağrısı (her biri iki satır)
   * demek: 200'lük tavan dört-beş kampanya turunda sohbeti kapatırdı.
   */
  maxMessagesPerConversation: 600,
  /**
   * Modele geri gönderilen geçmişin EN AZ satır sayısı.
   *
   * 🔴 Pencere her mesajda bir satır KAYMIYOR, `contextWindowStep` satırlık
   * adımlarla kayıyor (`agent-conversation.service.ts` → `replayWindowStart`).
   * Her kayma geçmişin başını değiştiriyor ve Opus 5.5'in korunan düşünmesi
   * önek değişince düşüyor; adım adım kayan pencere öneki `step` mesaj boyunca
   * SABİT tutuyor. Sürmekte olan turun tamamı her zaman pencerede.
   */
  contextWindowMessages: 60,
  /// Pencerenin başı kaç satırda bir ileri alınır — yukarıdaki notta gerekçe.
  contextWindowStep: 20,
  /**
   * SIKIŞTIRMA AÇIKKEN satır penceresi (Faz 9, 1 Eki 2026).
   *
   * Sunucu tarafı sıkıştırma (`compact_20260112`) konuşma bağlam bütçesini
   * aşınca eski kısmı ÖZETLİYOR; pencere o zaman satır sayısıyla değil
   * JETONLA yönetiliyor ve en son sıkıştırma bloğundan başlıyor (öncesini
   * sağlayıcı zaten düşürüyor). Bu tavan yalnız emniyet: sıkıştırma hiç
   * tetiklenmezse (kısa satırlar) istek ve veritabanı okuması sınırlı kalsın.
   * Aynı adım mantığı — tavanın başı `compactionWindowStep` satırda bir kayar.
   */
  compactionWindowMessages: 200,
  compactionWindowStep: 40,
  /// Dakika başına mesaj (throttle).
  messagesPerMinute: 10,
  /**
   * Bir yanıtta en fazla araç çağrısı — sonsuz döngüye karşı.
   *
   * 29 Eyl 2026: 8 → 30. Sohbetten kampanya kurmak (katalog araması, ürün,
   * kreatifler, hesap okuması, kampanya + reklam grubu + reklam operasyonları)
   * tek turda 20'yi rahatça geçiyor; 8'lik tavan turu yarıda kesiyordu.
   */
  maxToolCallsPerTurn: 30,
} as const;

export const AgentErrorCode = {
  /// `ANTHROPIC_API_KEY` yok. HTTP 503.
  LLM_NOT_CONFIGURED: 'LLM_NOT_CONFIGURED',
  CONVERSATION_NOT_FOUND: 'CONVERSATION_NOT_FOUND',
  /// Plan bazlı günlük mesaj limiti doldu. `details: {used, limit, resetsAt}`.
  DAILY_MESSAGE_LIMIT: 'DAILY_MESSAGE_LIMIT',
  /// Konuşma mesaj sayısı sınırına ulaştı.
  CONVERSATION_TOO_LONG: 'CONVERSATION_TOO_LONG',
  /// Araç bu rol için kapalı.
  TOOL_FORBIDDEN: 'TOOL_FORBIDDEN',
  TOOL_DISABLED: 'TOOL_DISABLED',
  PROPOSAL_NOT_FOUND: 'PROPOSAL_NOT_FOUND',
  PROPOSAL_EXPIRED: 'PROPOSAL_EXPIRED',
  PROPOSAL_ALREADY_DECIDED: 'PROPOSAL_ALREADY_DECIDED',
  /// Bu kart düzenlenemiyor (araç düzenleme kabul etmiyor ya da gövde
  /// kartın aracıyla eşleşmiyor). HTTP 400.
  PROPOSAL_NOT_EDITABLE: 'PROPOSAL_NOT_EDITABLE',
  /// Düzenleme sunucu doğrulamasından geçmedi (bilinmeyen şablon, başka
  /// çalışma alanının kreatifi…). `message` sebebi söylüyor. HTTP 400.
  PROPOSAL_EDIT_INVALID: 'PROPOSAL_EDIT_INVALID',
  /// Kartın kontrol listesinde onayı engelleyen bir madde var (eksik platform
  /// alanı, yetmeyen kredi). HTTP 409 — kart düzeltilmeden onaylanamaz.
  PROPOSAL_BLOCKED: 'PROPOSAL_BLOCKED',
  /// Kart yalnız KENDİ ekranından onaylanabilir (Faz 4): Meta kampanya kartı
  /// onayda bir Sayfa seçimi (`identityRef`) istiyor ve onay kuyruğu/API onu
  /// taşımıyor. HTTP 409 — kart YANMIYOR, beklemede kalıyor.
  PROPOSAL_NEEDS_CARD: 'PROPOSAL_NEEDS_CARD',
  /// Hızlı akışın cevapları sözleşmeye uymuyor (`checkAgentFlow`) — eksik
  /// zorunlu alan, bilinmeyen adım, görsel olmayan ek ya da kart cevabıyla
  /// birlikte gönderilmiş akış. `details: {step, problem}`. HTTP 400; hiçbir
  /// şey yazılmadı, günlük hak yanmadı (4 Eki 2026).
  AGENT_FLOW_INVALID: 'AGENT_FLOW_INVALID',
  /// Grace bitti; yazma araçları kapalı, okuma açık.
  WRITE_TOOLS_DISABLED: 'WRITE_TOOLS_DISABLED',
  /// Sağlayıcı hata verdi ve yeniden denemeler onu kurtaramadı. Ham metin
  /// kullanıcıya GİTMİYOR (17 Ağu 2026 kararı); ayrıntı yalnız sunucu
  /// kaydında. Kalıcı sınıf: 400 geçersiz istek, 401 anahtar, ağ kopması.
  PROVIDER_ERROR: 'PROVIDER_ERROR',
  /**
   * Sağlayıcı GEÇİCİ olarak kapasite dışı (`overloaded_error`, 429, 5xx) ve
   * geri çekilmeli yeniden denemeler de tükendi.
   *
   * `PROVIDER_ERROR`'den AYRI olmasının sebebi ekranda okunan cümle: burada
   * kullanıcının yapması gereken tek şey BEKLEMEK. Aynı kodu taşısaydı
   * "servis kullanılamıyor" derdi ve kullanıcı bir yapılandırma arızası
   * sanıp desteğe yazardı — oysa bir dakika sonra çalışacak.
   */
  PROVIDER_BUSY: 'PROVIDER_BUSY',
  /**
   * Sağlayıcının güvenlik sınıflandırıcısı isteği reddetti ve yedek model de
   * kurtaramadı (`stop_reason: "refusal"`). Yarım cevap SAKLANMIYOR.
   * Kullanıcı mesajını yeniden ifade edebilir; tekrar göndermek aynı sonucu
   * verir.
   */
  PROVIDER_REFUSED: 'PROVIDER_REFUSED',

  // ── Faz 4 — ajan kimliği ────────────────────────────────────────────────
  AGENT_NOT_FOUND: 'AGENT_NOT_FOUND',
  /// `@@unique([workspaceId, name])` ihlali. HTTP 409.
  AGENT_NAME_TAKEN: 'AGENT_NAME_TAKEN',
  /// `__` ön ekli ad — kullanıcı kendi ajanını kendi ekranından gizleyemesin.
  AGENT_NAME_RESERVED: 'AGENT_NAME_RESERVED',
  /// Bağlanmak istenen kural bu workspace'te yok ya da sistem kuralı.
  AGENT_WORKFLOW_NOT_ASSIGNABLE: 'AGENT_WORKFLOW_NOT_ASSIGNABLE',

  // ── AJ13 — mesaj eki ────────────────────────────────────────────────────
  /// Ek görselin S3 anahtarı BU çalışma alanına ait değil. HTTP 403.
  /// Sessizce atılmıyor: atılsaydı kullanıcı görselini gönderdiğini sanıp
  /// ajanın onu neden görmediğini anlamazdı.
  ATTACHMENT_NOT_OWNED: 'ATTACHMENT_NOT_OWNED',
  /// Ek okunamadı (depo kapalı ya da nesne yok). HTTP 503.
  ATTACHMENT_UNREADABLE: 'ATTACHMENT_UNREADABLE',
  /// Müşteri listesi kasada yok, süresi dolmuş ya da başka çalışma alanının. HTTP 404.
  CUSTOMER_LIST_NOT_FOUND: 'CUSTOMER_LIST_NOT_FOUND',
  /// CSV okunamadı (başlık yok, e-posta/telefon kolonu yok, boş). HTTP 400. `details.reason`.
  CUSTOMER_LIST_INVALID: 'CUSTOMER_LIST_INVALID',

  // ── Yanıt geri bildirimi ────────────────────────────────────────────────
  /// Oylanan mesaj yok, ajanın mesajı değil ya da başka kiracıya ait. HTTP 404.
  MESSAGE_NOT_FOUND: 'MESSAGE_NOT_FOUND',

  // ── Bağlantı kartı (30 Eyl 2026) ────────────────────────────────────────
  /// Kart cevabının `toolCallId`si bu sohbette o türde bir karta ait değil. HTTP 404.
  /// Tur AÇILMIYOR ve günlük hak YANMIYOR.
  CARD_NOT_FOUND: 'CARD_NOT_FOUND',

  // ── Kart eylemi ve canlı veri (Faz 8, 30 Eyl 2026) ──────────────────────
  /// Kartta bu kimlikte bir eylem yok (istemci eylem uyduramaz). HTTP 404.
  CARD_ACTION_NOT_FOUND: 'CARD_ACTION_NOT_FOUND',
  /// Eylem uçtan yürütülmez (`link` — istemcide açılır). HTTP 400.
  CARD_ACTION_NOT_EXECUTABLE: 'CARD_ACTION_NOT_EXECUTABLE',
  /// Girdi eylemin şemasına uymuyor, izinsiz alan ya da `choices` dışı değer. HTTP 400.
  CARD_ACTION_INVALID_INPUT: 'CARD_ACTION_INVALID_INPUT',
  /// Rol yetmiyor (hassas/yıkıcı = ADMIN). HTTP 403. `details.required`.
  CARD_ACTION_FORBIDDEN: 'CARD_ACTION_FORBIDDEN',
  /// Tek seferlik eylem zaten başarıyla yürüdü. HTTP 409.
  CARD_ACTION_DONE: 'CARD_ACTION_DONE',
  /// Aynı eylem şu an yürüyor (çift tıklama). HTTP 409.
  CARD_ACTION_IN_PROGRESS: 'CARD_ACTION_IN_PROGRESS',
  /// Bu kart türünün canlı veri kaynağı yok. HTTP 404.
  CARD_LIVE_UNAVAILABLE: 'CARD_LIVE_UNAVAILABLE',
  /// Canlı veriyi görmeye rol yetmiyor. HTTP 403. `details.required`.
  CARD_LIVE_FORBIDDEN: 'CARD_LIVE_FORBIDDEN',
} as const;
export type AgentErrorCode = (typeof AgentErrorCode)[keyof typeof AgentErrorCode];

/**
 * `/admin/ai` bu turda SALT OKUNUR.
 *
 * Sistem prompt'u, model seçimi, araç bayrakları ve günlük limit ezmesi için
 * kalıcı bir ayar deposu yok (`PlatformSetting` önerisi `semaDegisikligi`'nde)
 * ve `agent_conversations` bugün 0 satır. Yazılabilir bir editör, hiçbir
 * kodun okumadığı ayarları düzenleyen bir ekran olurdu. Ekran şu an geçerli
 * yapılandırmayı GÖSTERİR.
 */
export interface AdminAgentConfig {
  llmConfigured: boolean;
  model: string;
  /// Prompt'un kendisi değil, ilk 500 karakteri — destek "hangi sürüm
  /// çalışıyor" sorusunu cevaplasın, prompt kopyalanabilir olmasın.
  systemPromptPreview: string;
  tools: AgentToolDefinition[];
  dailyLimits: Record<string, number>;
  /// Tüm kiracılarda toplam konuşma/mesaj — bugün 0.
  usage: { conversations: number; messages: number; last7dMessages: number };
}

// ═══════════════════════════════════════════════════════════════════════════
// MENTORIVO EKRAN ARAÇLARI — panelin kendi ekranları sohbette (Faz 7, 30 Eyl 2026)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Ürün sahibinin kararı (30 Eyl 2026): *"Mentorivo ekranları sohbete
 * eklensin, güvenlik ayarları insanda kalsın."*
 *
 * AI 1095 platform işlemini yapabiliyordu ama panelin KENDİ ekranlarının
 * çoğunu (otomasyon düzenleme, ekip, bağlantılar, bildirimler, rakip analizi,
 * kreatif stüdyonun iyileştir/boyutlandır/manken araçları…) yapamıyordu. Bu
 * sözlük o ekranların sohbetteki karşılığı. Her araç MEVCUT servisi çağırıyor
 * — ikinci bir iş mantığı yok (`apps/api/src/agent/app-tools/`).
 *
 * ═══════════════════════════════════════════════════════════════════════
 * Neden `AGENT_TOOLS`a GİRMİYOR
 * ═══════════════════════════════════════════════════════════════════════
 * `AGENT_TOOLS` arayüzde TAM tiplenen bir liste (ikon sözlüğü,
 * `agent-i18n.spec.ts`) ve prompt önekinin başında her istekte duruyor. Bu
 * araçlar platform işlemleri gibi ERTELENMİŞ (`defer_loading` + araç arama);
 * birkaç çekirdek araç hariç modele yalnız arandığında yükleniyor.
 *
 * ═══════════════════════════════════════════════════════════════════════
 * 🔴 BİLİNÇLİ OLARAK OLMAYANLAR — insan işi
 * ═══════════════════════════════════════════════════════════════════════
 * API anahtarları, IP kısıtı, 2FA / güvenlik, sahiplik devri, çalışma alanı
 * silme, kart / ödeme / abonelik iptal-sürdürme, onay kuyruğunda KARAR,
 * otomatik pilot sınırları (`AutonomyPolicy`, hassasiyet anahtarları,
 * tavanlar), gelen webhook jetonu, bildirim iletişim adresleri ve mesajlaşma
 * kanalı BAĞLAMA (başka adrese yönlendirme = veri sızdırma yolu), mağaza
 * webhook kaydı, denetim/veri DIŞA AKTARMA, kendini ekipten çıkarma. Bunlar
 * için tek araç `panel_link`: kullanıcıya tıklayacağı sayfayı verir.
 *
 * Reklam platformu ve mağaza BAĞLAMA artık sohbette (30 Eyl 2026,
 * `connect_platform`): araç bir bağlantı KARTI çizer, OAuth'u / anahtar
 * formunu kullanıcı kartın içinden tamamlar. Sır yine AI'dan geçmez — anahtar
 * formdan doğrudan API'ye gider (`agent-connect.ts`).
 *
 * 🔴 Sıra SABİT ve SONA eklenir — araç listesi önbellek önekinin parçası.
 */
export const AGENT_APP_TOOLS = [
  // ── Otomasyon ──────────────────────────────────────────────────────────
  // 5 Eki 2026 — kural YAZAN araçlar kalktı (update, duplicate, run_now,
  // dry_run, validate, templates, install_template; çekirdekte
  // `create_workflow`). Kullanıcı kararı: AI kural kurmaz, operatör
  // devriyesiyle izler. Kalanlar var olan kuralı okur, duraklatır, siler ve
  // onay kuyruğunu gösterir — motor arkada çalışmaya devam ediyor.
  'automation_get',
  'automation_set_enabled',
  'automation_delete',
  'automation_logs',
  'automation_log_get',
  'automation_rollback',
  'automation_runs',
  'automation_run_get',
  'automation_approvals',
  'automation_approval_explain',
  'automation_for_campaign',
  // ── Rakip analizi ──────────────────────────────────────────────────────
  'competitor_profiles',
  'competitor_profile_create',
  'competitor_profile_update',
  'competitor_profile_delete',
  'competitor_sync',
  'competitor_ads',
  'competitor_insight_request',
  'competitor_insights',
  'competitor_insight_get',
  'auction_insights',
  // ── Kreatif stüdyo ─────────────────────────────────────────────────────
  'creative_options',
  'creative_estimate',
  'creative_prompt_improve',
  'creative_improve',
  'creative_tryon',
  'creative_banner',
  'creative_bulk_estimate',
  'creative_bulk_generate',
  'creative_get',
  'creative_download_link',
  'creative_delete',
  'creative_publish',
  'creative_request_approval',
  'creative_resize',
  'creative_upscale',
  'creative_regenerate',
  'brand_logos',
  'brand_logo_delete',
  'creative_jobs',
  'creative_job_get',
  'creative_job_cancel',
  'studio_models',
  'studio_model_create',
  'studio_model_delete',
  'product_vision_start',
  'product_vision_get',
  'creative_analysis',
  'creative_director',
  // ── Reklam bağlantıları ────────────────────────────────────────────────
  'ad_platforms',
  'ad_connections',
  'ad_connection_get',
  'ad_connection_discovery',
  'ad_connection_identities',
  'ad_connection_impact',
  'ad_sync_status',
  'ad_accounts_connect',
  'ad_account_update',
  'ad_account_sync',
  'ad_account_sync_errors',
  'ad_connection_disconnect',
  /* 1 Eki 2026 — tek reklam hesabını bağlantıdan çıkar (bağlantı kalır) ve
     demo veriyi kaldır: panelde vardı, sohbette yoktu ("buradan yapamam"). */
  'ad_account_remove',
  'demo_remove',
  // ── Mağaza ─────────────────────────────────────────────────────────────
  'store_platforms',
  'store_connections',
  'store_health',
  'store_sync_progress',
  'store_sync',
  'store_set_sync',
  'store_disconnect',
  'product_categories',
  'product_performance',
  'order_get',
  // ── Ekip ───────────────────────────────────────────────────────────────
  'team_list',
  'team_invite',
  'team_invite_resend',
  'team_invite_revoke',
  'team_change_role',
  'team_remove_member',
  // ── Bildirimler ────────────────────────────────────────────────────────
  'notifications_list',
  'notifications_unread_count',
  'notifications_mark_read',
  'notification_delete',
  'notification_preferences',
  'notification_preferences_update',
  'notification_types',
  'notification_deliveries',
  'messaging_channels',
  'messaging_test',
  // ── Rapor dışa aktarma ─────────────────────────────────────────────────
  'report_export_start',
  'report_export_status',
  // ── Analitik ───────────────────────────────────────────────────────────
  'analytics_summary',
  'analytics_timeseries',
  'analytics_breakdown',
  'analytics_ranking',
  'analytics_compare',
  'analytics_carbon',
  'analytics_metric_definitions',
  // ── Faturalama (yalnız OKUMA) ──────────────────────────────────────────
  'billing_subscription',
  'billing_usage',
  'billing_entitlements',
  'billing_soft_limit',
  'credits_ledger',
  'credits_usage',
  'credit_packs',
  'billing_plans',
  'credit_cost_table',
  // ── Özel ajanlar ───────────────────────────────────────────────────────
  'custom_agent_estimate',
  'custom_agents',
  'custom_agent_get',
  'custom_agent_kpi',
  'custom_agent_create',
  'custom_agent_update',
  'custom_agent_delete',
  'custom_agent_assign_workflows',
  'custom_agent_unassign_workflows',
  // ── Destek ─────────────────────────────────────────────────────────────
  'support_tickets',
  'support_ticket_get',
  'support_ticket_create',
  'support_ticket_reply',
  // ── Çalışma alanı ──────────────────────────────────────────────────────
  'workspaces_list',
  'workspace_roles',
  'workspace_settings',
  'workspace_platform_permissions',
  'workspace_context',
  'workspace_setup_status',
  'workspace_update_settings',
  'audit_log',
  // ── İnsan işi olan sayfaların bağlantısı ───────────────────────────────
  'panel_link',
  // ── Sohbetten bağlantı kartı (30 Eyl 2026) ─────────────────────────────
  'connect_platform',
  // ── Ek → araç köprüsü, dosya isteği, rakip arama (Faz 8, 30 Eyl 2026) ───
  'creative_import',
  'brand_logo_add',
  'studio_model_from_photo',
  'request_upload',
  'competitor_search',
  // ── Varlık içe aktarma: adresten indir + bağlı hesaplardan logo (1 Eki 2026) ──
  'asset_import_from_url',
  'brand_logo_from_platform',
  // ── Faz 8 E · operasyon ve hesap kartları (30 Eyl 2026) ────────────────
  'activity_feed',
  'account_balances',
  // ── Faz 8 A · analiz kartları (30 Eyl 2026) ────────────────────────────
  'get_breakdown',
  'diagnose_change',
  'forecast_budget',
  'suggest_budget_split',
  'store_sales',
  // ── Faz 8 D · yorum, lead, politika, dönüşüm takibi (30 Eyl 2026) ──────
  'list_ad_comments',
  'list_leads',
  'list_policy_issues',
  'tracking_health',
  // ── Kreatif stüdyo · reklam videosu = prodüksiyon (Faz 8a, 1 Eki 2026) ──
  /* Sohbet eşitliği (kullanıcı kararı): prodüksiyon sayfasında yapılabilen
     HER ŞEY sohbetten de — REST'in 14 ucunun karşılığı (`apps/api/src/
     creative/production/production.controller.ts`), AYNI servis yöntemleri. */
  'production_start',
  'production_get',
  'production_list',
  'production_answer',
  'production_pick_concept',
  'production_edit',
  'production_revise',
  'production_confirm_revision',
  'production_decline_revision',
  'production_approve',
  'production_retry_shot',
  'production_choose_take',
  'production_cancel',
  'production_deliverables',
  /* Dalga 4 (2 Eki 2026) — çıktı biçimi: tek reklam / açılış A/B seti /
     birkaç fikir. AI karar verir, kullanıcı onaydan önce fiyatı görüp
     değiştirir (`POST /creatives/productions/:id/strategy`). */
  'production_set_strategy',
  // ── Marka kiti + marka hafızası (Dalga 5, 2 Eki 2026) ──────────────────
  /* Kullanıcı kararı: kreatif stüdyosunun "Marka" sayfası (kit + AI notları)
     sohbetten de yönetilir ("markamın rengi şu", "bu tonu sevmedim"). */
  'brand_kit_get',
  'brand_kit_update',
  'brand_memory_list',
  'brand_memory_add',
  'brand_memory_delete',
  'brand_memory_pin',
  // ── Reklam performans döngüsü (Dalga 5, 2 Eki 2026) ────────────────────
  /* Varyantların A/B özeti, aynı reklam setine A/B olarak yayınlama ve
     yorulan reklamın yenilemesi (kaynak reklamlı prodüksiyon). */
  'production_performance',
  'production_publish_ab',
  'production_refresh',
] as const;
export type AgentAppToolName = (typeof AGENT_APP_TOOLS)[number];

export const AGENT_APP_TOOL_DOMAINS = [
  'automation',
  'competitor',
  'creative',
  'connections',
  'commerce',
  'team',
  'notifications',
  'exports',
  'analytics',
  'billing',
  'agents',
  'support',
  'workspace',
  'links',
] as const;
export type AgentAppToolDomain = (typeof AGENT_APP_TOOL_DOMAINS)[number];

export interface AgentAppToolDefinition {
  domain: AgentAppToolDomain;
  /**
   * `read` hemen çalışır. `write` HİÇBİR ŞEY UYGULAMAZ — `app_op` kartı
   * doğar; yürütme onayda (insan ya da dar otomatik pilot listesi).
   */
  kind: 'read' | 'write';
  /**
   * REST karşılığının yeteneği. `minRole` BUNDAN türüyor — elle seçilmiyor:
   * ekrandan yapabildiği şeyi sohbetten yapamayan (ya da tersine, ekranın
   * kapattığını sohbetten açan) bir rol olmasın. `null` = uç yalnız oturum
   * istiyor (kişinin kendi bildirimleri, destek talepleri).
   */
  capability: Capability | null;
  /// Çağırabilmek için en düşük rol — yetenekten ve (yazmada) `agent.write`ten.
  minRole: WorkspaceRole;
  /**
   * Kartı karara bağlayabilecek en düşük rol (yalnız yazma). Erişim, kesme,
   * ekip, silme gibi işler ADMIN — aracı çağırabilen herkes onaylayamaz.
   */
  decisionRole: WorkspaceRole;
  /**
   * OTOMATİK PİLOT onaysız uygulayabilir mi. DAR liste (ürün sahibi):
   * otomasyonu DURAKLATMA, bildirim okundu. Uygulanırsa `maxActionsPerDay`
   * kotasına sayılır ve denetime yazılır; ayrıntılı koşul (ör. yalnız
   * `enabled: false`) API tarafında (`app-tool.policy.ts`).
   */
  autopilot: boolean;
  /// Arka plan operatörü (başsız koşu) bu aracı görür mü.
  operator: boolean;
  /**
   * Sonuç KİŞİSEL VERİ taşıyor (ekip e-postaları, sipariş müşterisi, destek
   * talebi içeriği, denetim kaydı) — kalıcı kayıtta mühürlü/gizli; ham hâl
   * yalnız turun belleğinde (`pii-seal.ts` kemeri).
   */
  personalData: boolean;
  /// AI kredisi harcıyor — kart önce tahmin + bakiye gösterir, yetmezse kilitli.
  spendsCredits: boolean;
  /// İsteyen KİŞİYE bağlı (gelen kutusu, tercihler, destek) — operatörde yok.
  needsUser: boolean;
  /// Geri alınamaz (silme, bağlantı kesme) — karar ADMIN.
  irreversible: boolean;
  /// Ertelenmeyen (her istekte bağlamda) çekirdek araç.
  core: boolean;
  /**
   * HESAP BAKIMI (1 Eki 2026) — para/kredi harcamayan, harcama başlatamayan
   * yazma: bağlantı/hesap/mağaza/demo kaldırma, senkron anahtarı, ekip rolü ve
   * çıkarma, bildirim, destek talebi, kayıt silme. Abonelik bitince
   * (`agent.write` kapalı — `SUBSCRIPTION_INACTIVE`, ek süre doldu) de sohbette
   * AÇIK kalır: panelde de açık (REST karşılıkları aboneliğe bakmıyor) ve
   * tasarım §7 "veri rehin alınmaz". Kapanan şey ajanın PARA hareket ettiren
   * işleri (kampanya, bütçe, kreatif, kural açma…).
   *
   * Kök neden (1 Eki 2026, gerçek oturum): süresi dolmuş denemede araç listesi
   * BÜTÜN yazma araçlarını düşürüyordu; kullanıcı "By Nini bağlantısını kopar"
   * dedi, model `ad_connection_disconnect`i araç aramasında bulamadı ve "bu
   * sohbette bağlantı kaldırma açık değil, Bağlantılar sayfasına git" dedi.
   */
  housekeeping: boolean;
}

/** Rol tabanı: yazmalar en az `agent.write` (MEMBER). */
function maxRole(a: WorkspaceRole, b: WorkspaceRole): WorkspaceRole {
  return ROLE_RANK[a] >= ROLE_RANK[b] ? a : b;
}

function appTool(
  domain: AgentAppToolDomain,
  kind: 'read' | 'write',
  capability: Capability | null,
  options: Partial<
    Pick<
      AgentAppToolDefinition,
      | 'autopilot'
      | 'operator'
      | 'personalData'
      | 'spendsCredits'
      | 'needsUser'
      | 'irreversible'
      | 'core'
      | 'housekeeping'
    >
  > & { decisionRole?: WorkspaceRole } = {},
): AgentAppToolDefinition {
  const base: WorkspaceRole = capability ? CAPABILITY_MIN_ROLE[capability] : 'VIEWER';
  const minRole = kind === 'write' ? maxRole(base, CAPABILITY_MIN_ROLE['agent.write']) : base;
  const irreversible = options.irreversible ?? false;
  let decisionRole = maxRole(minRole, options.decisionRole ?? 'MEMBER');
  if (irreversible) decisionRole = maxRole(decisionRole, 'ADMIN');
  return {
    domain,
    kind,
    capability,
    minRole,
    decisionRole,
    autopilot: options.autopilot ?? false,
    /* Varsayılan: okumalar operatörde AÇIK, yazmalar KAPALI — yazma açan her
       satır bunu bilerek söylüyor. */
    operator: options.operator ?? kind === 'read',
    personalData: options.personalData ?? false,
    spendsCredits: options.spendsCredits ?? false,
    needsUser: options.needsUser ?? false,
    irreversible,
    core: options.core ?? false,
    /* Yalnız YAZMA araçlarında anlamlı — okumalar zaten her koşulda açık. */
    housekeeping: kind === 'write' && (options.housekeeping ?? false),
  };
}

/** Hesap bakımı (`housekeeping`) — kısaltma; gerekçe `AgentAppToolDefinition.housekeeping`. */
const HK = { housekeeping: true } as const;

/**
 * Yazma kilidinin PLAN/ABONELİK kaynaklı nedenleri (`EntitlementService` →
 * `agent.write`: abonelik yok, bitti, ek süre doldu). Hesap bakımı
 * (`housekeeping`) YALNIZ bu kilitte açık kalır. 🔴 Salt okunur koşu
 * (`READ_ONLY_FOLLOW_UP` — yönetici olmayanın takip görevi), kod içi çağrı
 * (`CODE_CALL_READ_ONLY`) ya da bilinmeyen neden HİÇBİR yazmaya izin vermez.
 */
export const AGENT_WRITE_PLAN_LOCK_REASONS = ['SUBSCRIPTION_INACTIVE', 'SUBSCRIPTION_MISSING', 'GRACE_EXPIRED'] as const;

/** Hesap bakımı yazmaları bu turda açık mı — araç listesi ve çağrı kapısı AYNI kural. */
export function housekeepingWritesOpen(writeAllowed: boolean, writeReason: string | null | undefined): boolean {
  return writeAllowed || (AGENT_WRITE_PLAN_LOCK_REASONS as readonly string[]).includes(writeReason ?? '');
}

const R = 'read' as const;
const W = 'write' as const;

export const AGENT_APP_TOOL_REGISTRY: Record<AgentAppToolName, AgentAppToolDefinition> = {
  // ── Otomasyon ──────────────────────────────────────────────────────────
  automation_get: appTool('automation', R, 'automation.read', { core: true }),
  /* Otomatik pilotun onaysız yapabildiği tek otomasyon işi: DURAKLATMA
     (`enabled: false`). Açmak her zaman kart. */
  automation_set_enabled: appTool('automation', W, 'automation.write', {
    autopilot: true,
    operator: true,
    core: true,
  }),
  automation_delete: appTool('automation', W, 'automation.write', { irreversible: true, ...HK }),
  automation_logs: appTool('automation', R, 'automation.read'),
  automation_log_get: appTool('automation', R, 'automation.read'),
  /* Geri alma `automation.approve` (ADMIN) — REST ile aynı. */
  automation_rollback: appTool('automation', W, 'automation.approve', { operator: true }),
  automation_runs: appTool('automation', R, 'automation.read'),
  automation_run_get: appTool('automation', R, 'automation.read'),
  /* Onay kuyruğu: LİSTELE ve AÇIKLA. 🔴 KARAR aracı YOK (insan işi). */
  automation_approvals: appTool('automation', R, 'automation.read'),
  automation_approval_explain: appTool('automation', R, 'automation.read', { operator: false }),
  automation_for_campaign: appTool('automation', R, 'automation.read'),

  // ── Rakip analizi ──────────────────────────────────────────────────────
  competitor_profiles: appTool('competitor', R, 'report.read'),
  competitor_profile_create: appTool('competitor', W, 'report.write'),
  competitor_profile_update: appTool('competitor', W, 'report.write'),
  competitor_profile_delete: appTool('competitor', W, 'report.write', { irreversible: true, ...HK }),
  competitor_sync: appTool('competitor', W, 'report.write'),
  competitor_ads: appTool('competitor', R, 'report.read'),
  competitor_insight_request: appTool('competitor', W, 'report.write', {
    spendsCredits: true,
    needsUser: true,
  }),
  competitor_insights: appTool('competitor', R, 'report.read'),
  competitor_insight_get: appTool('competitor', R, 'report.read'),
  auction_insights: appTool('competitor', R, 'report.read'),

  // ── Kreatif stüdyo ─────────────────────────────────────────────────────
  creative_options: appTool('creative', R, 'creative.read'),
  creative_estimate: appTool('creative', R, 'creative.generate'),
  /* Metin dönüşümü (LLM) — kredi yok, durum değişmiyor. */
  creative_prompt_improve: appTool('creative', R, 'creative.generate', {
    needsUser: true,
    operator: false,
  }),
  creative_improve: appTool('creative', W, 'creative.generate', { spendsCredits: true, needsUser: true }),
  creative_tryon: appTool('creative', W, 'creative.generate', { spendsCredits: true, needsUser: true }),
  creative_banner: appTool('creative', W, 'creative.generate', { spendsCredits: true, needsUser: true }),
  creative_bulk_estimate: appTool('creative', R, 'creative.generate'),
  creative_bulk_generate: appTool('creative', W, 'creative.generate', {
    spendsCredits: true,
    needsUser: true,
  }),
  creative_get: appTool('creative', R, 'creative.read'),
  creative_download_link: appTool('creative', R, 'creative.read', { operator: false }),
  creative_delete: appTool('creative', W, 'creative.delete', { irreversible: true, ...HK }),
  /* Reklama bağlamak platformda reklam doğurabilir — harcama başlatabilir. */
  creative_publish: appTool('creative', W, 'creative.publish'),
  creative_request_approval: appTool('creative', W, 'creative.generate', { needsUser: true }),
  creative_resize: appTool('creative', W, 'creative.generate', { spendsCredits: true, needsUser: true }),
  creative_upscale: appTool('creative', W, 'creative.generate', { spendsCredits: true, needsUser: true }),
  creative_regenerate: appTool('creative', W, 'creative.generate', {
    spendsCredits: true,
    needsUser: true,
  }),
  brand_logos: appTool('creative', R, 'creative.read'),
  brand_logo_delete: appTool('creative', W, 'creative.generate', { irreversible: true, ...HK }),
  creative_jobs: appTool('creative', R, 'creative.read'),
  creative_job_get: appTool('creative', R, 'creative.read'),
  creative_job_cancel: appTool('creative', W, 'creative.generate', { needsUser: true, ...HK }),
  studio_models: appTool('creative', R, 'creative.read'),
  studio_model_create: appTool('creative', W, 'creative.generate', {
    spendsCredits: true,
    needsUser: true,
  }),
  studio_model_delete: appTool('creative', W, 'creative.generate', { irreversible: true, ...HK }),
  product_vision_start: appTool('creative', W, 'creative.generate', {
    spendsCredits: true,
    needsUser: true,
  }),
  product_vision_get: appTool('creative', R, 'creative.generate'),
  creative_analysis: appTool('creative', R, 'campaign.read'),
  /* Stüdyo direktörünün form önerisi (LLM) — hiçbir şey kaydetmiyor. */
  creative_director: appTool('creative', R, 'creative.generate', { needsUser: true, operator: false }),

  // ── Reklam bağlantıları ────────────────────────────────────────────────
  ad_platforms: appTool('connections', R, 'connection.read'),
  ad_connections: appTool('connections', R, 'connection.read'),
  ad_connection_get: appTool('connections', R, 'connection.read'),
  /* Keşif platforma canlı istek atabiliyor (`refresh`) — REST'te ADMIN. */
  ad_connection_discovery: appTool('connections', R, 'connection.manage', { operator: false }),
  ad_connection_identities: appTool('connections', R, 'connection.read'),
  ad_connection_impact: appTool('connections', R, 'connection.read'),
  ad_sync_status: appTool('connections', R, 'connection.read'),
  /* Erişim işleri — HER ZAMAN onay + ADMIN, operatörde YOK. */
  ad_accounts_connect: appTool('connections', W, 'connection.manage'),
  ad_account_update: appTool('connections', W, 'connection.manage', HK),
  ad_account_sync: appTool('connections', W, 'connection.manage'),
  ad_account_sync_errors: appTool('connections', R, 'connection.read'),
  ad_connection_disconnect: appTool('connections', W, 'connection.manage', { irreversible: true, ...HK }),
  /* Tek reklam hesabını çıkar — bağlantı (jeton) kalır, aynı girişin başka
     hesabı seçilebilir (`ad_accounts_connect` / bağlantı kartı). REST
     `DELETE /integrations/accounts/:id`. Hesabın kampanya/metrik kopyası
     Mentorivo'dan silinir → geri alınamaz, ADMIN. */
  ad_account_remove: appTool('connections', W, 'connection.manage', { irreversible: true, ...HK }),
  /* Demo veriyi kaldır — REST `DELETE /demo` (yalnız demo işaretli satırlar).
     Demo yeri doluyken gerçek hesap bağlanamıyor; bağlantı kartı buraya
     yönlendiriyordu ("Bağlantılar sayfasından kaldır"). */
  demo_remove: appTool('connections', W, 'connection.manage', { irreversible: true, ...HK }),

  // ── Mağaza ─────────────────────────────────────────────────────────────
  store_platforms: appTool('commerce', R, 'connection.read'),
  store_connections: appTool('commerce', R, 'connection.read'),
  store_health: appTool('commerce', R, 'connection.read'),
  store_sync_progress: appTool('commerce', R, 'connection.read'),
  store_sync: appTool('commerce', W, 'connection.manage'),
  store_set_sync: appTool('commerce', W, 'connection.manage', HK),
  store_disconnect: appTool('commerce', W, 'connection.manage', { irreversible: true, ...HK }),
  product_categories: appTool('commerce', R, 'report.read'),
  product_performance: appTool('commerce', R, 'report.read'),
  order_get: appTool('commerce', R, 'report.read', { personalData: true, operator: false }),

  // ── Ekip — erişim işi: HER ZAMAN onay + ADMIN, operatörde YOK ──────────
  team_list: appTool('team', R, 'member.read', { personalData: true, operator: false }),
  team_invite: appTool('team', W, 'member.invite', { personalData: true }),
  team_invite_resend: appTool('team', W, 'member.invite'),
  team_invite_revoke: appTool('team', W, 'member.invite', HK),
  team_change_role: appTool('team', W, 'member.role_change', HK),
  team_remove_member: appTool('team', W, 'member.remove', { irreversible: true, ...HK }),

  // ── Bildirimler — kişinin kendi gelen kutusu ───────────────────────────
  notifications_list: appTool('notifications', R, null, { needsUser: true, operator: false }),
  notifications_unread_count: appTool('notifications', R, null, { needsUser: true, operator: false }),
  /* Otomatik pilot okundu işaretleyebilir (dar liste). */
  notifications_mark_read: appTool('notifications', W, null, { needsUser: true, autopilot: true, ...HK }),
  notification_delete: appTool('notifications', W, null, { needsUser: true, ...HK }),
  notification_preferences: appTool('notifications', R, null, { needsUser: true, operator: false }),
  notification_preferences_update: appTool('notifications', W, null, { needsUser: true, ...HK }),
  notification_types: appTool('notifications', R, null),
  notification_deliveries: appTool('notifications', R, null, { personalData: true, operator: false }),
  /* Varsayılan hedef (Slack kanalı / WhatsApp numarası) kalıcı kayıtta gizli. */
  messaging_channels: appTool('notifications', R, 'connection.read', { personalData: true }),
  /* Yalnız KAYITLI kanala sabit test mesajı — adres girdisi YOK. */
  messaging_test: appTool('notifications', W, 'connection.manage'),

  // ── Rapor dışa aktarma — iş başlatmak kayıt değiştirmiyor (okuma) ──────
  report_export_start: appTool('exports', R, 'campaign.read', { needsUser: true, operator: false }),
  report_export_status: appTool('exports', R, 'campaign.read', { operator: false }),

  // ── Analitik ───────────────────────────────────────────────────────────
  analytics_summary: appTool('analytics', R, 'campaign.read'),
  analytics_timeseries: appTool('analytics', R, 'campaign.read'),
  analytics_breakdown: appTool('analytics', R, 'campaign.read'),
  analytics_ranking: appTool('analytics', R, 'campaign.read'),
  analytics_compare: appTool('analytics', R, 'campaign.read'),
  analytics_carbon: appTool('analytics', R, 'campaign.read'),
  analytics_metric_definitions: appTool('analytics', R, null),

  // ── Faturalama — YALNIZ OKUMA ──────────────────────────────────────────
  billing_subscription: appTool('billing', R, 'billing.read'),
  billing_usage: appTool('billing', R, 'billing.read'),
  billing_entitlements: appTool('billing', R, null),
  billing_soft_limit: appTool('billing', R, null),
  credits_ledger: appTool('billing', R, 'billing.read'),
  credits_usage: appTool('billing', R, 'billing.read'),
  credit_packs: appTool('billing', R, null),
  billing_plans: appTool('billing', R, null),
  credit_cost_table: appTool('billing', R, null),

  // ── Özel ajanlar ───────────────────────────────────────────────────────
  custom_agent_estimate: appTool('agents', R, 'automation.read'),
  custom_agents: appTool('agents', R, 'automation.read'),
  custom_agent_get: appTool('agents', R, 'automation.read'),
  custom_agent_kpi: appTool('agents', R, 'automation.read'),
  custom_agent_create: appTool('agents', W, 'automation.write'),
  custom_agent_update: appTool('agents', W, 'automation.write'),
  custom_agent_delete: appTool('agents', W, 'automation.write', { irreversible: true, ...HK }),
  custom_agent_assign_workflows: appTool('agents', W, 'automation.write'),
  custom_agent_unassign_workflows: appTool('agents', W, 'automation.write'),

  // ── Destek — talep içeriği kişisel veri ────────────────────────────────
  support_tickets: appTool('support', R, 'workspace.read', { personalData: true, operator: false }),
  support_ticket_get: appTool('support', R, 'workspace.read', { personalData: true, operator: false }),
  support_ticket_create: appTool('support', W, 'workspace.read', { needsUser: true, personalData: true, ...HK }),
  support_ticket_reply: appTool('support', W, 'workspace.read', { needsUser: true, personalData: true, ...HK }),

  // ── Çalışma alanı ──────────────────────────────────────────────────────
  workspaces_list: appTool('workspace', R, null, { needsUser: true, operator: false }),
  workspace_roles: appTool('workspace', R, null),
  workspace_settings: appTool('workspace', R, 'workspace.read'),
  workspace_platform_permissions: appTool('workspace', R, 'connection.read'),
  workspace_context: appTool('workspace', R, 'workspace.read', { needsUser: true, operator: false }),
  workspace_setup_status: appTool('workspace', R, 'workspace.read', { needsUser: true, operator: false }),
  /* Yalnız güvenlik/otonomi DIŞI alanlar (ad, sektör, saat dilimi, atıf). */
  workspace_update_settings: appTool('workspace', W, 'workspace.update', HK),
  /* Denetim kaydını OKUMA serbest (ADMIN); DIŞA AKTARMA yok. */
  audit_log: appTool('workspace', R, 'audit.read', { personalData: true, operator: false }),

  // ── Bağlantı ───────────────────────────────────────────────────────────
  panel_link: appTool('links', R, null, { core: true }),
  /* BAĞLANTI KARTI — okuma: hiçbir şey değiştirmiyor, kredi harcamıyor, onay
     kartı DEĞİL. Durumu okuyup sohbete kartı çiziyor; bağlamayı kullanıcı
     kartın içinden (platformun kendi izin ekranında) yapıyor. Çekirdek: "TikTok
     reklamı açacağız" cümlesinde araç aramasına gerek kalmadan elde olmalı.
     `needsUser`: karşıda tıklayacak bir insan şart — arka plan operatörü ve
     API anahtarıyla gelen sohbet bu aracı görmüyor. Kartı herkes görür
     (`connection.read`); düğme `connection.manage` ister (kart rolü söylüyor). */
  connect_platform: appTool('connections', R, 'connection.read', {
    core: true,
    needsUser: true,
    operator: false,
  }),

  // ── Ek → araç köprüsü (Faz 8, 30 Eyl 2026) ─────────────────────────────
  /* Üçü de sohbetteki bir EKE (`a1`) dayanıyor: ekin sahibi konuşan kişi,
     arka plan operatöründe ek yok (`needsUser`). Kredi harcamıyor (hazır
     dosya kütüphaneye/logo listesine/manken listesine alınıyor); yazma olduğu
     için kart — onay ya da otomatik pilot politikası. */
  creative_import: appTool('creative', W, 'creative.generate', { needsUser: true }),
  brand_logo_add: appTool('creative', W, 'creative.generate', { needsUser: true }),
  studio_model_from_photo: appTool('creative', W, 'creative.generate', { needsUser: true }),
  /* DOSYA İSTEĞİ KARTI — okuma: hiçbir şey değiştirmiyor; sohbete yükleme
     alanı olan bir kart çiziyor. Karşıda dosyayı seçecek bir insan şart. */
  request_upload: appTool('creative', R, null, { needsUser: true, operator: false }),
  /* Marka adıyla Meta Reklam Kütüphanesi'nde SAYFA arama — rakip takibine
     sayısal sayfa kimliği sormadan başlanabilsin. Platform jetonu ortak:
     sunucu önbellekliyor ve çalışma alanı başına sınırlıyor. */
  competitor_search: appTool('competitor', R, 'report.read'),

  // ── Varlık içe aktarma (1 Eki 2026) ────────────────────────────────────
  /* ADRESTEN İÇE AKTARMA — `brand_logo_add`/`creative_import` ile aynı kural:
     yazma, kart, kredi YOK. Sahnelemede dosya GÜVENLİ indiriciden
     (`common/net/safe-download.ts`) geçip geçici yükleme alanına iniyor ve
     kart onu önizliyor; kütüphaneye/logo listesine yalnız ONAYDA giriyor.
     Kişiye bağlı (`needsUser`): arka plan operatörü ve kural bağlayamaz. */
  asset_import_from_url: appTool('creative', W, 'creative.generate', { needsUser: true }),
  /* BAĞLI HESAPLARDAN LOGO — okuma: platformların profil görsellerini
     (Sayfa, Instagram, LinkedIn şirket sayfası, TikTok kimliği, X profili)
     okuyup aday kartı çiziyor. Adaylar geçici alana indiği için eşik
     `creative.generate` (kaydetme düğmesiyle aynı rol); karşıda seçecek bir
     insan şart — operatörde YOK. */
  brand_logo_from_platform: appTool('creative', R, 'creative.generate', { needsUser: true, operator: false }),

  // ── Faz 8 E · operasyon ve hesap kartları (30 Eyl 2026) ────────────────
  /* AI'IN YAPTIKLARI — eylem günlüğü + operatör koşuları + denetim kaydının
     kart/geri alma olayları, tek zaman çizelgesi. Okuma; geri alma kartın
     DÜĞMESİ (insan: `automation_rollback` / bilinen ters katalog işlemi). */
  activity_feed: appTool('automation', R, 'automation.read'),
  /* Reklam hesabı bakiyeleri — platform OKUMA işlemleri (`meta.account.get`,
     `tiktok.account.get`, `google.billing.list_account_budgets`,
     `x.funding.list`, `linkedin.account.billing`); REST `/platform-ops`
     okumasıyla aynı eşik. Transfer kartın düğmesi (FUNDS → ADMIN). */
  account_balances: appTool('billing', R, 'campaign.read'),

  // ── Faz 8 A · analiz kartları (30 Eyl 2026) ────────────────────────────
  /* Beş platformun kırılımı TEK çağrıda, ortak boyut adlarıyla — platform
     OKUMA işlemleri (`meta.insights.query`, `google.report.query`,
     `tiktok.report.run`, `linkedin.report.performance`); REST `/platform-ops`
     okumasıyla aynı eşik. */
  get_breakdown: appTool('analytics', R, 'campaign.read'),
  /* KPI değişiminin çarpan ayrıştırması + takip kopması şüphesi — SUNUCUDA
     (model sayı üretmesin). Yalnız analitik okuması. */
  diagnose_change: appTool('analytics', R, 'campaign.read'),
  /* Platformun kendi tahmin/simülasyon okuması → tek bütçe eğrisi. "Uygula"
     kartın DÜĞMESİ (insan; bütçe işlemi `campaign.write`). */
  forecast_budget: appTool('analytics', R, 'campaign.read'),
  /* Kural motorunun AYNI dağıtım algoritması (`planRedistribution`) — öneri;
     uygulama kartın düğmesi ya da onaylı işlem planı. */
  suggest_budget_split: appTool('analytics', R, 'campaign.read'),
  /* Mağaza cirosu/siparişi + reklam harcaması (MER). Sipariş MÜŞTERİSİ
     okunmuyor — yalnız tutar ve tarih. */
  store_sales: appTool('analytics', R, 'report.read'),

  // ── Faz 8 D · yorum, lead, politika, dönüşüm takibi (30 Eyl 2026) ──────
  /* Beş platformun reklam yorumları TEK çağrıda (`comments/comment-collect.ts`
     — gözcünün okuma dili). Sonuç yorum METNİ ve YAZARI taşıyor: tur içinde
     modele çerçeveli (güvenilmez içerik), kalıcı kayıtta gizli
     (`personalData` + `APP_PII_RESULT_KEYS`). Kart satırı yalnız kimlik. */
  list_ad_comments: appTool('analytics', R, 'campaign.read', { personalData: true }),
  /* Lead listesi — MODELE yalnız kimlik + sayı; cevaplar (ad, e-posta,
     telefon) kartın canlı ucunda, rol denetimli. Nitelik bildirimi kartın
     düğmesi (kişisel veri parametreli işlem → ADMIN). */
  list_leads: appTool('analytics', R, 'campaign.read'),
  /* Reddedilen/kısıtlı reklamlar — yerel senkron + platformun inceleme
     okuması. İtiraz/muafiyet kartın düğmesi (insan). */
  list_policy_issues: appTool('analytics', R, 'campaign.read'),
  /* Piksel / etiket / CAPI / dönüşüm eylemi sağlığı — platform okumaları +
     yerel metrik (harcama var dönüşüm yok). */
  tracking_health: appTool('analytics', R, 'campaign.read'),

  // ── Kreatif stüdyo · reklam videosu = prodüksiyon (Faz 8a, 1 Eki 2026) ──
  /* REST'le AYNI yetenek eşiği (`production.controller.ts`): okuma
     `creative.read` (izleyici süren videoyu takip edebilir), her yazma
     `creative.generate`. 🔴 YAZMALARIN HEPSİ kişiye bağlı (`needsUser`):
     kredi rızası (başlat, onay, revizyon rızası, yeniden çekim) ve kapı
     kararları (cevap, konsept, take) bir İNSANIN — arka plan operatörü
     prodüksiyon açamaz/ilerletemez. Yorgunluk → varyant bağı (Dalga 5):
     operatörün gördüğü tek başlatma `production_refresh` (aşağıda — kaynak
     reklamlı yenileme, onay kuyruğu kartı). */
  production_start: appTool('creative', W, 'creative.generate', { spendsCredits: true, needsUser: true }),
  production_get: appTool('creative', R, 'creative.read'),
  production_list: appTool('creative', R, 'creative.read'),
  production_answer: appTool('creative', W, 'creative.generate', { needsUser: true }),
  production_pick_concept: appTool('creative', W, 'creative.generate', { needsUser: true }),
  production_edit: appTool('creative', W, 'creative.generate', { needsUser: true }),
  /* Revizyon kendisi kredi ÇEKMİYOR: onaydan sonra maliyetliyse ayrı rıza
     kapısı açılır (`production_confirm_revision`, kendi rezervi). */
  production_revise: appTool('creative', W, 'creative.generate', { needsUser: true }),
  production_confirm_revision: appTool('creative', W, 'creative.generate', { spendsCredits: true, needsUser: true }),
  production_decline_revision: appTool('creative', W, 'creative.generate', { needsUser: true }),
  production_approve: appTool('creative', W, 'creative.generate', { spendsCredits: true, needsUser: true }),
  production_retry_shot: appTool('creative', W, 'creative.generate', { spendsCredits: true, needsUser: true }),
  production_choose_take: appTool('creative', W, 'creative.generate', { needsUser: true }),
  /* İptal harcamayı DURDURUR (o ana kadarki gerçek maliyet, kalanı iade) —
     `creative_job_cancel` gibi hesap bakımı: abonelik bitse de açık. */
  production_cancel: appTool('creative', W, 'creative.generate', { needsUser: true, ...HK }),
  production_deliverables: appTool('creative', R, 'creative.read'),
  /* Biçim değişikliği kendisi kredi ÇEKMİYOR: yeni varyantların taslağı
     storyboard rezervinden (kapandıysa harcanan-kesilmemiş olarak onay
     tutarına) gider; fiyat onay ekranında yeniden sorulur. Yine de bir
     İNSANIN kararı — onay tutarını büyütür (`needsUser`). Dalga 5: TASLAKTAN
     ÖNCE AI zarfını aşan biçim (birkaç fikir, 4–5 açılış) EK taslak kredisi
     REZERVE eder (`production:<id>:plan:<n>`) — kart o tutarı ve bakiyeyi
     gösterir (`spendsCredits`; ek tutar yoksa bedel satırı çıkmaz). */
  production_set_strategy: appTool('creative', W, 'creative.generate', { spendsCredits: true, needsUser: true }),

  // ── Marka kiti + marka hafızası (Dalga 5, 2 Eki 2026) ──────────────────
  /* REST'le AYNI eşik: okuma `creative.read`, yazma `creative.generate`
     (Marka sayfası kreatif stüdyosunun parçası). Kit ve notlar her üretimin
     dosyasına giriyor — sesi/rengi/yasakları DEĞİŞTİRMEK bir İNSANIN kararı
     (`needsUser`): arka plan operatörü markayı kendiliğinden yeniden
     yazamaz. Not silme/sabitleme kayıt bakımı (`HK` — kredi yok). */
  brand_kit_get: appTool('creative', R, 'creative.read'),
  brand_kit_update: appTool('creative', W, 'creative.generate', { needsUser: true }),
  brand_memory_list: appTool('creative', R, 'creative.read'),
  brand_memory_add: appTool('creative', W, 'creative.generate', { needsUser: true }),
  brand_memory_delete: appTool('creative', W, 'creative.generate', { needsUser: true, ...HK }),
  brand_memory_pin: appTool('creative', W, 'creative.generate', { needsUser: true, ...HK }),

  // ── Reklam performans döngüsü (Dalga 5, 2 Eki 2026) ────────────────────
  /* Varyant A/B özeti — metrik okuması, prodüksiyon sayfasının paneliyle aynı uç. */
  production_performance: appTool('creative', R, 'creative.read'),
  /* A/B YAYINI kredi harcamaz ama PLATFORMA yazar (aynı reklam setinde
     varyant başına bir reklam, DURAKLATILMIŞ) — `creative_publish`in eşiği
     (`creative.publish`). Operatör önerebilir (yenilemenin teslimi sonrası);
     uygulama her zaman kart, etkinleştirme ayrı ve politikalı adım. */
  production_publish_ab: appTool('creative', W, 'creative.publish', { operator: true }),
  /* YENİLEME = kaynak reklamlı yeni prodüksiyon (açılış seti). Kredi harcar.
     🔴 `needsUser` DEĞİL: yorgunluk olayında operatör SAHNELER (onay kuyruğu
     kartı); onaysız başlatma yalnız tam otonomi + otomatik pilot + sahibin
     açtığı günlük kreatif kredi tavanı içinde (`maxCreativeCreditsPerDay`,
     varsayılan 0 = asla) — politika API'de. */
  production_refresh: appTool('creative', W, 'creative.generate', { spendsCredits: true, operator: true }),
};

export function isAgentAppTool(name: string): name is AgentAppToolName {
  return Object.prototype.hasOwnProperty.call(AGENT_APP_TOOL_REGISTRY, name);
}

/** `app_op` kartının özel gövdesi — `AgentAppOpDraft.display`. */
export type AgentAppOpView = { kind: 'disconnect_impact' } & DisconnectImpactView;

/**
 * EKRAN İŞLEMİ kartının içeriği — `AgentProposal.after.draft` (`app_op`).
 *
 * Kartın GÖSTERDİĞİ alanlar `after`ın kendisinde (önce → sonra satırları,
 * genel kartın `DiffBody`si); bu taslak yürütmenin ihtiyacı. Sunucu kuruyor
 * — model hiçbir alanını doğrudan yazmıyor.
 */
export interface AgentAppOpDraft {
  tool: AgentAppToolName;
  domain: AgentAppToolDomain;
  /**
   * Aracın DOĞRULANMIŞ girdisi. Yürütücü onayda AYNI şemadan yeniden
   * geçiriyor (kart ile onay arasında şema değişmiş olabilir). Mühürlü
   * alanlar yer tutucu (`sealed`).
   */
  input: Record<string, unknown>;
  /**
   * İşin ADINA yapıldığı kişi — kişisel gelen kutusu, tercihler, destek
   * talebi, kredi rezervasyonu sahibi. İSTEYEN kişi; onaylayan değil (bir
   * yönetici başkasının kartını onaylarsa işlem onun bildirimlerine
   * uygulanmasın).
   */
  forUserId: string | null;
  /// Kredi harcayan işte tahmin (kart bedel satırı).
  credits: { cost: number; available: number } | null;
  /// `block` onayı engelliyor (sunucu + kart) — ör. `credits.short`.
  checks: { code: string; level: 'block' | 'warn' }[];
  irreversible: boolean;
  /// Kişisel veri mührü (e-posta, destek metni) — `pii-seal.ts`.
  sealed: { ref: string; fields: string[]; expiresAt: string } | null;
  /// Uygulandıktan sonra: tek satır özet + modelin zinciri sürdüreceği kimlikler.
  result: { summary: string; data?: Record<string, unknown> } | null;
  /**
   * KARTIN ÖZEL GÖVDESİ (1 Eki 2026) — genel "önce → sonra" satırları yerine
   * çizilen dar görünüm. Bugün tek tür: kaldırma ETKİSİ (bağlantı, reklam
   * hesabı, mağaza, demo) — onaylayan kişi neyin silineceğini kartın İÇİNDE
   * görsün, ayrı bir ham tablo kartında değil. Eski satırlarda alan yok.
   * (Adı `view` DEĞİL: kural taslağı `RuleAppOpDraft.view` başka bir şey.)
   */
  display?: AgentAppOpView | null;
  failure?: { code: string; message: string } | null;
}
